<template>
  <div class="home-container">
    <div v-if="maintenanceMode" class="hero maintenance-hero">
      <div class="hero-content">
        <h1 class="hero-title">{{ t("message.maintenance.title") }}</h1>
        <p class="hero-subtitle">{{ t("message.maintenance.description") }}</p>
        <p v-if="contactEmail" class="hero-subtitle">
          {{ t("message.maintenance.contact", { email: contactEmail }) }}
        </p>
      </div>
    </div>

    <template v-else>
      <CourseDiscovery
        :locale="state.locale"
        :compact="!showHero"
        :show-add-course="!isCourseDetailView"
        :compact-title="
          isCourseDetailView
            ? selectedCourses[0]?.course_name || $route.params.courseCode
            : ''
        "
        :courses="isCourseCatalogUnavailable ? [] : searchableCourses"
        @select="handleSearch"
      >
        <div class="course-builder">
          <SearchBar
            :items="searchableCourses"
            :showAddButton="true"
            :disabled="isCourseCatalogUnavailable"
            :loading="isLoadingCourses"
            @search="handleSearch"
            @add="handleSearch"
            :key="`builder-search-${search_rerender_key}`"
            class="builder-search-bar"
          />
          <p
            v-if="!isLoadingCourses && !courseCatalogError"
            class="quick-add-hint"
          >
            {{ t("message.dashboard.searchToAdd") }}
          </p>
          <div
            v-if="isLoadingCourses || courseCatalogError"
            class="course-builder-status"
            :class="{ 'course-builder-status-error': courseCatalogError }"
            role="status"
            aria-live="polite"
          >
            <div class="course-builder-status-row">
              <span
                v-if="isLoadingCourses"
                class="course-builder-status-spinner"
                aria-hidden="true"
              ></span>
              <span>
                {{
                  courseCatalogError ||
                  (showSlowBackendHint
                    ? t("message.api.backendWakingUp")
                    : t("message.api.loadingCourses"))
                }}
              </span>
            </div>
            <button
              v-if="courseCatalogError"
              class="toggle-button course-builder-retry-button"
              @click="retryLoadCourses"
            >
              {{ t("message.api.retry") }}
            </button>
          </div>
        </div>
      </CourseDiscovery>
      <router-link
        v-if="$route.query.source === 'explore'"
        class="catalog-return-link"
        :to="{
          name: 'CourseCatalog',
          query: {
            institution: selection.initialized
              ? selection.institution
              : $route.query.institution,
            program: selection.initialized
              ? selection.program
              : $route.query.program,
          },
        }"
        >←
        {{
          ui(
            "Tilbake til emnelisten og kortlisten",
            "Back to your course list and shortlist"
          )
        }}</router-link
      >
      <router-link
        v-if="$route.query.source === 'rankings'"
        class="catalog-return-link"
        :to="{
          name: 'EasiestHardestCourses',
          query: {
            institution: selection.initialized
              ? selection.institution
              : $route.query.institution,
            program: selection.initialized
              ? selection.program
              : $route.query.program,
          },
        }"
        >← {{ ui("Tilbake til rangeringen", "Back to rankings") }}</router-link
      >
      <p
        v-if="!courseSelected && !showHero"
        class="selection-status"
        role="status"
        aria-live="polite"
      >
        {{
          dashboardMessage ||
          courseCatalogError ||
          (isLoadingCourses || isRestoringDashboard || isFetchingCourseStats
            ? t("message.api.loadingCourseData")
            : "")
        }}
      </p>
      <section v-if="courseSelected" class="result-section">
        <v-row justify="center">
          <v-col cols="12" md="12">
            <v-card class="result-card" elevation="0">
              <div class="workspace-toolbar">
                <div class="workspace-status">
                  <span class="workspace-dot"></span
                  ><strong
                    >{{ isCourseDetailView ? $route.params.courseCode : selectedCourses.length }}
                    {{ isCourseDetailView ? '' : ui("fag i oversikten", "courses in your workspace") }}</strong
                  ><span>{{
                    isCourseDetailView
                      ? ui("Emneoversikt", "Course overview")
                      : ui(
                          "Lagres automatisk på denne enheten",
                          "Automatically saved on this device"
                        )
                  }}</span>
                </div>
                <div
                  v-if="!isCourseDetailView"
                  class="workspace-layout"
                  :aria-label="ui('Oppsett for oversikten', 'Workspace layout')"
                >
                  <button
                    type="button"
                    :aria-pressed="!isCombinedView"
                    @click="setWorkspaceLayout('cards')"
                  >
                    {{ ui("Fagkort", "Course cards") }}
                  </button>
                  <button
                    type="button"
                    :aria-pressed="isCombinedView"
                    :disabled="visibleSelectedCourses.length < 2"
                    @click="setWorkspaceLayout('combined')"
                  >
                    {{ ui("Felles graf", "Shared chart") }} ↗
                  </button>
                </div>
                <div v-if="!isCourseDetailView" class="workspace-actions">
                  <details class="workspace-menu">
                    <summary
                      :aria-label="
                        ui('Handlinger for oversikten', 'Workspace actions')
                      "
                    >
                      •••
                    </summary>
                    <div>
                      <button
                        type="button"
                        @click="
                          isDashboardToolsExpanded = !isDashboardToolsExpanded
                        "
                      >
                        {{ ui("Flere verktøy", "More tools") }}
                      </button>
                      <button type="button" @click="saveDashboardState()">
                        {{ t("message.dashboard.saveDashboard") }}</button
                      ><button
                        type="button"
                        :disabled="!savedDashboardAvailable"
                        @click="restoreDashboardFromStorage(true)"
                      >
                        {{ t("message.dashboard.loadDashboard") }}</button
                      ><button type="button" @click="clearDashboardCourses">
                        {{ t("message.dashboard.clearCourses") }}
                      </button>
                    </div>
                  </details>
                </div>
              </div>
              <div class="workspace-controls">
                <div
                  class="workspace-modes"
                  :aria-label="
                    ui('Visning for alle fag', 'View for all courses')
                  "
                >
                  <button
                    v-for="option in workspaceModes"
                    :key="option.value"
                    type="button"
                    :aria-pressed="
                      selectedCourses.every(
                        (card) => card.mode === option.value
                      )
                    "
                    @click="setAllCardModes(option.value)"
                  >
                    {{ option.label }}
                  </button>
                </div>
                <label
                  v-if="
                    selectedCourses.some((card) => card.mode !== 'distribution')
                  "
                  class="workspace-range"
                  >{{ ui("Historikk", "History")
                  }}<select v-model.number="historyYears">
                    <option :value="0">{{ ui("Alle år", "All years") }}</option>
                    <option :value="5">
                      {{ ui("Siste 5 dataår", "Last 5 data years") }}
                    </option>
                    <option :value="10">
                      {{ ui("Siste 10 dataår", "Last 10 data years") }}
                    </option>
                  </select></label
                >
                <details
                  v-if="
                    selectedCourses.some((card) => card.mode !== 'failRates')
                  "
                  class="workspace-calculation"
                >
                  <summary>
                    {{
                      excludeFails
                        ? ui("Snitt uten F", "Average excludes F")
                        : ui("Snitt med F", "Average includes F")
                    }}
                    <v-icon size="15" aria-hidden="true"
                      >mdi-chevron-down</v-icon
                    >
                  </summary>
                  <div>
                    <label class="workspace-check"
                      ><input type="checkbox" v-model="excludeFails" />{{
                        ui(
                          "Utelat F fra snittet",
                          "Exclude F from the average"
                        )
                      }}</label
                    >
                    <p>
                      {{
                        ui(
                          "Karakterfordelingen og strykandelen inkluderer fortsatt F.",
                          "Grade distributions and failure rates still include F."
                        )
                      }}
                    </p>
                  </div>
                </details>
                <div
                  v-if="
                    selectedCourses.length > 1 &&
                    selectedCourses.every(
                      (card) => card.mode === 'distribution'
                    )
                  "
                  class="workspace-period"
                >
                  <label
                    ><span>{{ ui("Periode", "Period") }}</span
                    ><select
                      :aria-label="ui('Periode', 'Period')"
                      v-model="comparisonTerm"
                      @change="applyComparisonTerm"
                    >
                      <option value="">
                        {{
                          ui(
                            "Periode per fag",
                            "Period per course"
                          )
                        }}
                      </option>
                      <option
                        v-for="term in commonTerms"
                        :key="term.key"
                        :value="term.key"
                      >
                        {{ term.label }}
                      </option>
                    </select></label
                  ><span v-if="!commonTerms.length">{{
                    ui(
                      "Fagene har ingen felles registrert periode.",
                      "These courses have no shared observed period."
                    )
                  }}</span>
                </div>
              </div>
              <p v-if="dashboardMessage" class="dashboard-message">
                {{ dashboardMessage }}
              </p>
              <p
                v-if="isFetchingCourseStats"
                class="dashboard-message dashboard-loading-message"
                role="status"
                aria-live="polite"
              >
                <span
                  class="dashboard-loading-spinner"
                  aria-hidden="true"
                ></span>
                {{
                  showSlowBackendHint
                    ? t("message.api.backendWakingUp")
                    : t("message.api.loadingCourseData")
                }}
              </p>
              <p v-if="dashboardAtCapacity" class="dashboard-capacity-note">
                {{
                  t("message.dashboard.limitReached", {
                    max: maxDashboardCharts,
                  })
                }}
              </p>

              <div
                v-if="showPremiumDashboardBanner"
                class="dashboard-premium-note"
              >
                <p class="dashboard-premium-copy">
                  {{ t("message.dashboard.premiumLocked") }}
                </p>
                <v-btn
                  v-if="state.features.premiumEnabled"
                  color="primary"
                  size="small"
                  to="/premium"
                >
                  {{ t("message.dashboard.unlockPremium") }}
                </v-btn>
              </div>

              <transition name="dashboard-tools-fade">
                <aside
                  v-if="isDashboardToolsExpanded"
                  class="dashboard-tools dashboard-tools-overlay"
                >
                  <div class="dashboard-tools-header">
                    <div>
                      <h4 class="dashboard-tools-title">
                        {{ t("message.dashboard.toolsTitle") }}
                      </h4>
                      <p class="dashboard-tools-subtitle">
                        {{ t("message.dashboard.toolsSubtitle") }}
                      </p>
                      <div class="dashboard-tools-summary">
                        <v-tooltip location="bottom" max-width="320">
                          <template #activator="{ props }">
                            <span
                              class="dashboard-summary-chip dashboard-summary-chip-help"
                              v-bind="props"
                            >
                              {{
                                t("message.dashboard.summarySmartPicks", {
                                  count: dashboardSmartPickCount,
                                  total: dashboardInsights.length,
                                })
                              }}
                              <v-icon
                                size="13"
                                class="dashboard-summary-help-icon"
                              >
                                mdi-help-circle-outline
                              </v-icon>
                            </span>
                          </template>
                          <span>{{
                            t("message.dashboard.summarySmartPicksHelp")
                          }}</span>
                        </v-tooltip>
                        <v-tooltip location="bottom" max-width="320">
                          <template #activator="{ props }">
                            <span
                              class="dashboard-summary-chip dashboard-summary-chip-help"
                              v-bind="props"
                            >
                              {{ t("message.dashboard.summaryStability") }}:
                              {{
                                dashboardAverageStability == null
                                  ? "—"
                                  : formatPercent(dashboardAverageStability)
                              }}
                              <v-icon
                                size="13"
                                class="dashboard-summary-help-icon"
                              >
                                mdi-help-circle-outline
                              </v-icon>
                            </span>
                          </template>
                          <span>{{
                            t("message.dashboard.summaryStabilityHelp")
                          }}</span>
                        </v-tooltip>
                        <v-tooltip location="bottom" max-width="320">
                          <template #activator="{ props }">
                            <span
                              class="dashboard-summary-chip dashboard-summary-chip-help"
                              v-bind="props"
                            >
                              {{
                                t("message.dashboard.summaryOfferProbability")
                              }}:
                              {{
                                dashboardAverageOfferProbability == null
                                  ? "—"
                                  : formatPercent(
                                      dashboardAverageOfferProbability
                                    )
                              }}
                              <v-icon
                                size="13"
                                class="dashboard-summary-help-icon"
                              >
                                mdi-help-circle-outline
                              </v-icon>
                            </span>
                          </template>
                          <span>{{
                            t("message.dashboard.summaryOfferProbabilityHelp")
                          }}</span>
                        </v-tooltip>
                      </div>
                    </div>
                    <button
                      class="dashboard-collapse-toggle dashboard-collapse-toggle-inline"
                      @click="isDashboardToolsExpanded = false"
                    >
                      {{ t("message.dashboard.collapseTools") }}
                    </button>
                  </div>
                  <div class="dashboard-tools-nav">
                    <button
                      v-for="tab in dashboardToolTabs"
                      :key="tab.value"
                      class="dashboard-tools-tab"
                      :class="{
                        'dashboard-tools-tab-active':
                          activeDashboardTool === tab.value,
                      }"
                      @click="activeDashboardTool = tab.value"
                    >
                      {{ tab.label }}
                    </button>
                  </div>
                  <div class="dashboard-tools-grid dashboard-tools-grid-single">
                    <section
                      v-if="activeDashboardTool === 'view'"
                      class="dashboard-tools-group"
                    >
                      <p class="dashboard-tools-label">
                        {{ t("message.dashboard.viewOptions") }}
                      </p>
                      <div class="toggle-buttons dashboard-tool-buttons">
                        <button
                          v-if="hasHistoricalChart"
                          @click="excludeFails = !excludeFails"
                          :class="{ active: excludeFails }"
                          class="toggle-button"
                        >
                          {{
                            excludeFails
                              ? t("message.includeFails")
                              : t("message.excludeFails")
                          }}
                        </button>
                        <button
                          @click="showFemaleStats = !showFemaleStats"
                          :class="{ active: showFemaleStats }"
                          class="toggle-button"
                        >
                          {{
                            showFemaleStats
                              ? t("message.hideFemaleStats")
                              : t("message.showFemaleStats")
                          }}
                        </button>
                        <button
                          @click="
                            filterOfferedBothSemesters =
                              !filterOfferedBothSemesters
                          "
                          :class="{ active: filterOfferedBothSemesters }"
                          class="toggle-button"
                        >
                          {{
                            filterOfferedBothSemesters
                              ? t("message.dashboard.filterBothSemestersOff")
                              : t("message.dashboard.filterBothSemestersOn")
                          }}
                        </button>
                      </div>
                      <p class="dashboard-tools-note">
                        {{ t("message.dashboard.filterBothSemestersHelp") }}
                      </p>
                      <p class="dashboard-tools-label">
                        {{ t("message.dashboard.programFocusTitle") }}
                      </p>
                      <p class="dashboard-tools-note">
                        {{ t("message.dashboard.programFocusHelp") }}
                      </p>
                      <div
                        class="dashboard-tools-row dashboard-tools-row-program-focus"
                      >
                        <select
                          v-model="dashboardProgramFilterInstitutionCode"
                          class="dropdown dashboard-tools-select"
                          @change="onDashboardProgramInstitutionChange"
                        >
                          <option
                            v-for="option in dashboardProgramInstitutionOptions"
                            :key="option.value"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                        <select
                          v-model="dashboardProgramFilterStudyProgramCode"
                          class="dropdown dashboard-tools-select"
                          :disabled="!canSelectDashboardStudyProgram"
                          @change="onDashboardProgramStudyProgramChange"
                        >
                          <option
                            v-for="option in dashboardProgramStudyProgramOptions"
                            :key="option.value"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </div>
                      <p
                        v-if="
                          isDashboardProgramFilterActive &&
                          dashboardProgramFilteredHiddenCount > 0
                        "
                        class="dashboard-tools-note"
                      >
                        {{
                          t("message.dashboard.programFocusHiddenCount", {
                            count: dashboardProgramFilteredHiddenCount,
                          })
                        }}
                      </p>
                      <p
                        v-if="
                          isDashboardProgramFilterActive &&
                          dashboardProgramVisibleCount === 0
                        "
                        class="dashboard-tools-note"
                      >
                        {{ t("message.dashboard.programFocusEmpty") }}
                      </p>
                      <p
                        v-if="
                          filterOfferedBothSemesters &&
                          filteredOutCourseCount > 0
                        "
                        class="dashboard-tools-note"
                      >
                        {{
                          t(
                            "message.dashboard.filterBothSemestersHiddenCount",
                            {
                              count: filteredOutCourseCount,
                            }
                          )
                        }}
                      </p>
                      <p class="dashboard-tools-note">
                        {{ t("message.dashboard.bulkChartMode") }}
                      </p>
                      <div class="dashboard-tools-row">
                        <button
                          class="toggle-button"
                          :disabled="!selectedCourses.length"
                          @click="setAllCardModes('historical')"
                        >
                          {{ t("message.dashboard.setAllHistorical") }}
                        </button>
                        <button
                          class="toggle-button"
                          :disabled="!selectedCourses.length"
                          @click="setAllCardModes('distribution')"
                        >
                          {{ t("message.dashboard.setAllDistribution") }}
                        </button>
                        <button
                          class="toggle-button"
                          :disabled="!selectedCourses.length"
                          @click="setAllCardModes('failRates')"
                        >
                          {{ t("message.dashboard.setAllFailRates") }}
                        </button>
                      </div>
                    </section>

                    <section
                      v-else-if="activeDashboardTool === 'style'"
                      class="dashboard-tools-group"
                    >
                      <label for="palette-select" class="dashboard-tools-label">
                        {{ t("message.dashboard.visualPalette") }}
                      </label>
                      <select
                        id="palette-select"
                        v-model="selectedPaletteKey"
                        class="dropdown dashboard-tools-select"
                      >
                        <option
                          v-for="palette in chartPaletteOptions"
                          :key="palette.value"
                          :value="palette.value"
                        >
                          {{ palette.label }}
                        </option>
                      </select>
                    </section>

                    <section
                      v-else-if="activeDashboardTool === 'advanced'"
                      class="dashboard-tools-group"
                    >
                      <p class="dashboard-tools-label">
                        {{ t("message.dashboard.advancedTitle") }}
                      </p>
                      <p class="dashboard-tools-note">
                        {{ t("message.dashboard.advancedSubtitle") }}
                      </p>
                      <template v-if="canUseAdvancedDashboardTools">
                        <label
                          for="dashboard-risk-profile"
                          class="dashboard-tools-label"
                        >
                          {{ t("message.premium.risk") }}
                        </label>
                        <select
                          id="dashboard-risk-profile"
                          v-model="dashboardRiskProfile"
                          class="dropdown dashboard-tools-select"
                        >
                          <option
                            v-for="option in dashboardRiskProfileOptions"
                            :key="option.value"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                        <div
                          v-if="dashboardRiskRewardPoints.length"
                          class="risk-reward-map"
                        >
                          <p class="risk-reward-map-title">
                            {{ t("message.dashboard.riskRewardMapTitle") }}
                          </p>
                          <p class="risk-reward-map-subtitle">
                            {{ t("message.dashboard.riskRewardMapSubtitle") }}
                          </p>
                          <p class="risk-reward-map-hint">
                            {{ t("message.dashboard.riskRewardMapClickHint") }}
                          </p>
                          <div class="risk-reward-map-canvas">
                            <span
                              v-for="tick in riskRewardAxisTicks"
                              :key="`risk-x-tick-${tick}`"
                              class="risk-reward-axis-tick risk-reward-axis-tick-x"
                              :style="{ left: `${toMapAxisPercent(tick)}%` }"
                            >
                              {{ tick }}
                            </span>
                            <button
                              v-for="point in dashboardRiskRewardPoints"
                              :key="`risk-map-${point.course_code}`"
                              type="button"
                              class="risk-reward-point"
                              :class="`risk-reward-point-${point.quadrant}`"
                              :style="{
                                left: `${point.riskPercent}%`,
                                top: `${100 - point.rewardPercent}%`,
                              }"
                              :title="getRiskRewardPointTitle(point)"
                              :aria-label="getRiskRewardPointTitle(point)"
                              @click="handleRiskRewardPointClick(point)"
                            >
                              {{
                                formatRiskRewardPointLabel(point.course_code)
                              }}
                            </button>
                            <span
                              class="risk-reward-axis risk-reward-axis-x-left"
                            >
                              {{ t("message.dashboard.riskLow") }}
                            </span>
                            <span
                              class="risk-reward-axis risk-reward-axis-x-right"
                            >
                              {{ t("message.dashboard.riskHigh") }}
                            </span>
                            <span
                              class="risk-reward-axis risk-reward-axis-y-top"
                            >
                              {{ t("message.dashboard.rewardHigh") }}
                            </span>
                            <span
                              class="risk-reward-axis risk-reward-axis-y-bottom"
                            >
                              {{ t("message.dashboard.rewardLow") }}
                            </span>
                            <span
                              class="risk-reward-axis-title risk-reward-axis-title-x"
                            >
                              {{ t("message.dashboard.riskAxisLabel") }}
                            </span>
                            <span
                              class="risk-reward-axis-title risk-reward-axis-title-y"
                            >
                              {{ t("message.dashboard.rewardAxisLabel") }}
                            </span>
                          </div>
                          <div class="risk-reward-method">
                            <p class="risk-reward-method-title">
                              {{ t("message.dashboard.riskRewardMethodTitle") }}
                            </p>
                            <p class="risk-reward-method-line">
                              {{
                                t("message.dashboard.riskRewardMethodReward")
                              }}
                            </p>
                            <p class="risk-reward-method-line">
                              {{ t("message.dashboard.riskRewardMethodRisk") }}
                            </p>
                            <p class="risk-reward-method-line">
                              {{
                                t("message.dashboard.riskRewardMethodQuadrant")
                              }}
                            </p>
                          </div>
                        </div>
                        <div
                          v-if="dashboardShortlist.length"
                          class="dashboard-shortlist"
                        >
                          <article
                            v-for="item in dashboardShortlist"
                            :key="`short-${item.course_code}`"
                            class="dashboard-shortlist-item"
                          >
                            <div class="dashboard-shortlist-head">
                              <strong>{{ item.course_code }}</strong>
                              <span
                                class="dashboard-shortlist-quadrant"
                                :class="`dashboard-shortlist-quadrant-${item.quadrant}`"
                              >
                                {{
                                  t(
                                    `message.advanced.quadrants.${item.quadrant}`
                                  )
                                }}
                              </span>
                            </div>
                            <p class="dashboard-shortlist-meta">
                              {{ item.course_name }}
                            </p>
                            <p class="dashboard-shortlist-meta">
                              {{ t("message.advanced.metrics.passRate") }}:
                              {{ formatPercent(item.passRate) }}
                              ·
                              {{
                                t("message.advanced.metrics.offerProbability")
                              }}:
                              {{ formatPercent(item.offerProbability) }}
                              · {{ t("message.dashboard.signalStability") }}:
                              {{ formatPercent(item.stabilityScore) }}
                            </p>
                            <p
                              v-if="item.offeredBothSemesters"
                              class="dashboard-shortlist-meta dashboard-shortlist-semesters"
                            >
                              {{
                                t(
                                  "message.dashboard.signalOfferedBothSemesters"
                                )
                              }}
                            </p>
                            <div class="dashboard-shortlist-actions">
                              <button
                                class="panel-plan-btn panel-plan-btn-short"
                                :class="{
                                  'panel-plan-btn-saved': isCourseInStudyPlan(
                                    item.course_code
                                  ),
                                }"
                                :disabled="isSyncingStudyPlan"
                                @click="toggleStudyPlanCourse(item.course_code)"
                              >
                                {{
                                  isCourseInStudyPlan(item.course_code)
                                    ? t(
                                        "message.dashboard.studyPlanRemoveLabel"
                                      )
                                    : t("message.dashboard.addToStudyPlan")
                                }}
                              </button>
                            </div>
                          </article>
                        </div>
                        <p v-else class="dashboard-tools-note">
                          {{ t("message.dashboard.noAdvancedSignals") }}
                        </p>
                      </template>
                      <p v-else class="dashboard-tools-lock">
                        {{ t("message.dashboard.advancedPremiumLocked") }}
                      </p>
                    </section>

                    <section v-else class="dashboard-tools-group">
                      <p class="dashboard-tools-label">
                        {{ t("message.dashboard.exportTitle") }}
                      </p>
                      <p class="dashboard-tools-note">
                        {{ t("message.dashboard.exportSubtitle") }}
                      </p>
                      <div class="dashboard-tools-row">
                        <button
                          class="toggle-button"
                          :disabled="
                            !isPremiumDashboardUnlocked ||
                            !selectedCourses.length
                          "
                          @click="exportDashboardCsv"
                        >
                          {{ t("message.dashboard.exportCsv") }}
                        </button>
                        <button
                          class="toggle-button"
                          :disabled="
                            !isPremiumDashboardUnlocked ||
                            !selectedCourses.length
                          "
                          @click="exportDashboardJson"
                        >
                          {{ t("message.dashboard.exportJson") }}
                        </button>
                      </div>
                      <p
                        v-if="!isPremiumDashboardUnlocked"
                        class="dashboard-tools-lock"
                      >
                        {{ t("message.dashboard.exportLocked") }}
                      </p>
                    </section>
                  </div>
                </aside>
              </transition>

              <p
                v-if="
                  filterOfferedBothSemesters && !visibleSelectedCourses.length
                "
                class="dashboard-filter-empty-note"
              >
                {{ t("message.dashboard.filterBothSemestersEmpty") }}
              </p>
              <CourseComparison
                v-if="isCombinedView"
                :items="comparisonItems"
                :mode="selectedCourses[0]?.mode || 'distribution'"
                :chart-options="
                  getChartOptionsForMode(
                    selectedCourses[0]?.mode || 'distribution'
                  )
                "
                :locale="state.locale"
                :exclude-fails="excludeFails"
                :women-enabled="showFemaleStats"
                :can-align="commonTerms.length > 0"
                @remove="removeCourseCard"
                @mode="setAllCardModes"
                @align="setAllCardModes('distribution')"
                @cards="setWorkspaceLayout('cards')"
              />
              <div
                v-if="!isCombinedView && visibleSelectedCourses.length"
                class="dashboard-grid"
                :class="{
                  'dashboard-grid-single': visibleSelectedCourses.length === 1,
                }"
              >
                <CourseInsight
                  v-for="courseCard in visibleSelectedCourses"
                  :key="courseCard.id"
                  :id="`dashboard-course-card-${courseCard.id}`"
                  :course="courseCard"
                  :removable="!isCourseDetailView"
                  :rows="getCardRowsForChart(courseCard)"
                  :terms="getCardTermOptions(courseCard)"
                  :chart-data="getChartDataForCard(courseCard)"
                  :chart-options="getChartOptionsForMode(courseCard.mode)"
                  :locale="state.locale"
                  :exclude-fails="excludeFails"
                  :can-plan="canUseStudyPlanFeature"
                  :in-plan="isCourseInStudyPlan(courseCard.course_code)"
                  @mode="setIndividualMode(courseCard, $event)"
                  @term="setIndividualTerm(courseCard, $event)"
                  @remove="removeCourseCard(courseCard.id)"
                  @plan="toggleStudyPlanCourse(courseCard.course_code)"
                />
              </div>
              <details class="workspace-method">
                <summary>
                  {{ ui("Om sammenligningen", "About this comparison") }}
                </summary>
                <p>
                  {{
                    ui(
                      "Samme skala på alle grafer. Manglende observasjoner vises som hull. Nøkkeltall gjelder perioden som står på hvert fag. Registrerte resultater er ikke nødvendigvis unike studenter.",
                      "All charts use the same scale. Missing observations appear as gaps. Key figures refer to the period shown on each course. Recorded results are not necessarily unique students."
                    )
                  }}
                </p>
              </details>
            </v-card>
          </v-col>
        </v-row>
      </section>
    </template>
  </div>
</template>

<script>
import { inject } from "vue";
import { useRoute } from "vue-router";
import ApiService from "@/ApiService";
import SearchBar from "@/components/SearchBar.vue";
import CourseDiscovery from "@/components/CourseDiscovery.vue";
import CourseInsight from "@/components/CourseInsight.vue";
import CourseComparison from "@/components/CourseComparison.vue";
import { courseSelection } from "@/courseSelection";
import {
  numberOrNull,
  rateOrNull,
  distributionForRow,
  averageForRow,
  alignTerms,
  sortTerms,
  termKey,
  metricSampleSize,
  sampleLevel,
  hasAmbiguousTerms,
} from "@/utils/gradeStatistics.mjs";
import LZString from "lz-string";

const CACHE_KEY = "allCourses_v3";
const TIMESTAMP_KEY = "coursesTimestamp_v3";
const CACHE_DURATION = 28 * 24 * 60 * 60 * 1000;
const CONTACT_EMAIL = process.env.VUE_APP_CONTACT_EMAIL || "";
const IS_PRODUCTION_BUILD = process.env.NODE_ENV === "production";
const envFlag = (value, defaultValue = false) => {
  if (value == null || value === "") return defaultValue;
  return ["1", "true", "yes", "on"].includes(String(value).toLowerCase());
};
const DEV_STUDY_PLAN_MOCK_ENABLED = envFlag(
  process.env.VUE_APP_DEV_MOCK_STUDY_PLAN,
  !IS_PRODUCTION_BUILD
);
const DEV_STUDY_PLAN_STORAGE_KEY = "onlygrades_dev_study_plan_codes";
const MAINTENANCE_MODE = envFlag(process.env.VUE_APP_MAINTENANCE_MODE, false);
const DASHBOARD_MAX_FREE_CHARTS = 2;
const DASHBOARD_MAX_PREMIUM_CHARTS = 8;
const DEFAULT_CARD_MODE = "distribution";
const DASHBOARD_PROGRAM_FILTER_ALL = "__ALL__";
const DASHBOARD_STORAGE_KEY_PREFIX = "onlygrades_dashboard_v1";
const DEFAULT_CHART_PALETTE = "onlygrades";
const CHART_PALETTES = {
  onlygrades: {
    lines: {
      historical: "#397454",
      historicalFemale: "#8b5c89",
      failRates: "#b47932",
      failRatesFemale: "#9a4560",
    },
    baseBars: [
      "#397454",
      "#72916a",
      "#b3ca79",
      "#cbb66e",
      "#c49365",
      "#a56757",
      "#517b80",
      "#85718f",
    ],
    femaleBars: [
      "#295c40",
      "#5b7b52",
      "#8b9e51",
      "#a58d42",
      "#a47648",
      "#894d40",
      "#3c6368",
      "#6c5778",
    ],
  },
  slate: {
    lines: {
      historical: "#334155",
      historicalFemale: "#475569",
      failRates: "#0f766e",
      failRatesFemale: "#b45309",
    },
    baseBars: [
      "#334155",
      "#0f766e",
      "#2563eb",
      "#9333ea",
      "#b45309",
      "#0f766e",
      "#1d4ed8",
      "#64748b",
    ],
    femaleBars: [
      "#1e293b",
      "#115e59",
      "#1e40af",
      "#6d28d9",
      "#92400e",
      "#134e4a",
      "#1d4ed8",
      "#475569",
    ],
  },
  warm: {
    lines: {
      historical: "#c2410c",
      historicalFemale: "#be185d",
      failRates: "#0284c7",
      failRatesFemale: "#7c2d12",
    },
    baseBars: [
      "#c2410c",
      "#be185d",
      "#0284c7",
      "#b45309",
      "#6d28d9",
      "#0f766e",
      "#2563eb",
      "#9f1239",
    ],
    femaleBars: [
      "#9a3412",
      "#9d174d",
      "#0369a1",
      "#92400e",
      "#5b21b6",
      "#115e59",
      "#1d4ed8",
      "#881337",
    ],
  },
};

export default {
  name: "HomePage",
  components: {
    SearchBar,
    CourseDiscovery,
    CourseInsight,
    CourseComparison,
  },
  setup() {
    const state = inject("state");
    const route = useRoute();
    const ui = (no, en) => (state.locale === "no" ? no : en);

    const t = (key, params = {}) => {
      const keys = key.split(".");
      let value = state.translations[state.locale];
      keys.forEach((k) => {
        value = value?.[k];
      });
      if (typeof value === "string") {
        return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => {
          const paramValue = params[paramKey];
          return paramValue == null ? `{{${paramKey}}}` : String(paramValue);
        });
      }
      return value;
    };

    return {
      t,
      ui,
      state,
      route,
      contactEmail: CONTACT_EMAIL,
      maintenanceMode: MAINTENANCE_MODE,
      selection: courseSelection.state,
    };
  },
  data() {
    return {
      workspaceLayout: "combined",
      selectionReady: false,
      dashboardGeneration: 0,
      historyYears: 5,
      comparisonTerm: "",
      allCourses: [],
      initialSearchDone: false,
      excludeFails: false,
      showFemaleStats: false,
      filterOfferedBothSemesters: false,
      search_rerender_key: 0,
      dashboardMessage: "",
      selectedCourses: [],
      studyPlanCourseCodes: [],
      selectedPaletteKey: DEFAULT_CHART_PALETTE,
      activeDashboardTool: "view",
      dashboardRiskProfile: "balanced",
      dashboardProgramFilterInstitutionCode: DASHBOARD_PROGRAM_FILTER_ALL,
      dashboardProgramFilterStudyProgramCode: DASHBOARD_PROGRAM_FILTER_ALL,
      isDashboardToolsExpanded: false,
      nextCourseCardId: 1,
      savedDashboardAvailable: false,
      isRestoringDashboard: false,
      isDashboardDisposed: false,
      isSyncingStudyPlan: false,
      isLoadingCourses: false,
      courseCatalogError: "",
      pendingCourseStatsRequests: 0,
      pendingApiRequests: 0,
      showSlowBackendHint: false,
      slowBackendTimerId: null,
      focusedCourseCardId: null,
      focusResetTimerId: null,
    };
  },
  async created() {
    if (!this.maintenanceMode) {
      if (!this.hasRouteCourseSelection(this.$route)) {
        if (this.selection.initialized)
          this.initialSearchDone = this.selection.courses.length > 0;
        else
          try {
            const saved = JSON.parse(
              localStorage.getItem(this.getDashboardStorageKey()) || "{}"
            );
            this.initialSearchDone =
              Array.isArray(saved.courses) && saved.courses.length > 0;
          } catch {
            /* A saved workspace is optional. */
          }
      }
      await this.loadCourses();
      if (this.isDashboardDisposed) return;
      await this.loadStudyPlanCourses();
      if (this.isDashboardDisposed) return;
      this.refreshSavedDashboardAvailability();
      if (!this.hasRouteCourseSelection(this.$route)) {
        await this.restoreDashboardFromStorage(false);
      }
      this.selectionReady = true;
    }
  },
  mounted() {
    if (this.maintenanceMode) return;
    this.applyRouteCourseSelection(this.$route);
  },
  beforeUnmount() {
    this.isDashboardDisposed = true;
    this.dashboardGeneration++;
    this.clearSlowBackendTimer();
    if (this.focusResetTimerId) {
      clearTimeout(this.focusResetTimerId);
      this.focusResetTimerId = null;
    }
  },
  watch: {
    "selection.revision"() {
      if (
        this.selectionReady &&
        !this.isDashboardDisposed &&
        !this.isCourseDetailView
      )
        this.syncFromSharedSelection();
    },
    workspaceLayout() {
      this.persistDashboardState();
    },
    historyYears() {
      this.comparisonTerm = "";
      this.refreshAllDistributionSelections();
      this.persistDashboardState();
    },

    $route(to, from) {
      if (this.maintenanceMode) return;
      if (
        from?.params?.courseCode &&
        to.name === "home" &&
        !this.hasRouteCourseSelection(to)
      )
        return this.restoreDashboardFromStorage(false);
      this.applyRouteCourseSelection(to);
    },
    selectedCourses: {
      handler() {
        this.ensureDashboardProgramFilterSelection();
        this.persistDashboardState();
      },
      deep: true,
    },
    excludeFails() {
      this.persistDashboardState();
    },
    showFemaleStats() {
      this.persistDashboardState();
    },
    filterOfferedBothSemesters() {
      this.persistDashboardState();
    },
    selectedPaletteKey(nextValue) {
      if (!this.isPaletteValid(nextValue)) {
        this.selectedPaletteKey = DEFAULT_CHART_PALETTE;
        return;
      }
      this.persistDashboardState();
    },
    dashboardRiskProfile(nextValue) {
      if (!["low", "balanced", "high"].includes(nextValue)) {
        this.dashboardRiskProfile = "balanced";
        return;
      }
      this.persistDashboardState();
    },
    dashboardProgramFilterInstitutionCode(nextValue) {
      const normalizedCode = this.normalizeInstitutionCode(nextValue);
      if (normalizedCode !== nextValue) {
        this.dashboardProgramFilterInstitutionCode = normalizedCode;
        return;
      }
      this.ensureDashboardProgramFilterSelection();
      this.refreshAllDistributionSelections();
      this.persistDashboardState();
    },
    dashboardProgramFilterStudyProgramCode(nextValue) {
      const normalizedCode = this.normalizeStudyProgramCode(nextValue);
      if (normalizedCode !== nextValue) {
        this.dashboardProgramFilterStudyProgramCode = normalizedCode;
        return;
      }
      this.ensureDashboardProgramFilterSelection();
      this.refreshAllDistributionSelections();
      this.persistDashboardState();
    },
    isDashboardToolsExpanded() {
      this.persistDashboardState();
    },
    "state.authToken": {
      handler() {
        this.loadStudyPlanCourses();
      },
    },
    maxDashboardCharts(nextLimit) {
      if (this.selectedCourses.length <= nextLimit) return;
      this.selectedCourses = this.selectedCourses.slice(0, nextLimit);
      this.dashboardMessage =
        !this.state.features.premiumEnabled || this.isPremiumDashboardUnlocked
          ? this.t("message.dashboard.limitReached", { max: nextLimit })
          : this.t("message.dashboard.premiumLocked");
    },
  },
  methods: {
    commitSharedSelection(context = null, preserveMissing = false) {
      // Viewing a course must not replace the saved comparison or shortlist.
      if (this.isCourseDetailView) return;
      context =
        context ||
        (this.selection.initialized ? this.selection : this.$route.query);
      if (context?.institution != null)
        courseSelection.setContext({
          institution: context.institution,
          program: context.program || "",
        });
      if (!courseSelection.state.institution && this.selectedCourses.length) {
        const institutions = new Set(
          this.selectedCourses
            .flatMap((card) =>
              card.program_offerings.map(
                (offering) => offering.institution_code
              )
            )
            .filter(Boolean)
        );
        if (institutions.size === 1)
          courseSelection.setContext({
            institution: [...institutions][0],
            program: "",
          });
      }
      courseSelection.replaceCourses(
        preserveMissing
          ? [...this.selection.courses, ...this.selectedCourses]
          : this.selectedCourses
      );
    },
    async syncFromSharedSelection() {
      if (this.isCourseDetailView) return;
      const codes = this.selection.courses.map((course) => course.course_code);
      if (
        codes.join(",") ===
        this.selectedCourses.map((course) => course.course_code).join(",")
      )
        return;
      if (!codes.length) {
        this.clearDashboardCourses();
        return;
      }
      await this.applyRouteCourseSelection({
        params: {},
        query: {
          ...this.$route.query,
          courses: codes.join(","),
          institution: this.selection.institution,
          program: this.selection.program,
          layout: this.workspaceLayout,
        },
      });
    },
    setWorkspaceLayout(layout) {
      if (layout === "combined")
        this.setAllCardModes(this.selectedCourses[0]?.mode || "distribution");
      this.workspaceLayout = layout;
      this.syncSelectionToRoute();
    },

    setIndividualMode(card, mode) {
      card.mode = mode;
      this.onCardModeChange(card);
      this.comparisonTerm = "";
    },
    setIndividualTerm(card, key) {
      card.selectedTermKey = key;
      this.onCardTermSelectChange(card);
      this.comparisonTerm = "";
    },
    applyComparisonTerm() {
      if (!this.comparisonTerm) return;
      this.selectedCourses.forEach((card) => {
        const term = this.getCardTermOptions(card).find(
          (option) => option.key === this.comparisonTerm
        );
        if (term) this.setCardTermSelection(card, term);
      });
    },

    clearCourseCache() {
      try {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(TIMESTAMP_KEY);
      } catch (error) {
        void error;
      }
    },
    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    startSlowBackendTimer() {
      if (this.pendingApiRequests !== 1) return;
      this.clearSlowBackendTimer();
      this.showSlowBackendHint = false;
      this.slowBackendTimerId = window.setTimeout(() => {
        if (this.pendingApiRequests > 0) {
          this.showSlowBackendHint = true;
        }
      }, 1600);
    },
    clearSlowBackendTimer() {
      if (!this.slowBackendTimerId) return;
      clearTimeout(this.slowBackendTimerId);
      this.slowBackendTimerId = null;
    },
    beginApiRequest() {
      this.pendingApiRequests += 1;
      this.startSlowBackendTimer();
    },
    endApiRequest() {
      this.pendingApiRequests = Math.max(0, this.pendingApiRequests - 1);
      if (this.pendingApiRequests === 0) {
        this.clearSlowBackendTimer();
        this.showSlowBackendHint = false;
      }
    },
    isRetryableApiError(error) {
      if (String(error?.message || "") === "Invalid course data") {
        return false;
      }
      const statusCode = Number(error?.response?.status || 0);
      if ([408, 425, 429, 500, 502, 503, 504].includes(statusCode)) {
        return true;
      }
      const errorCode = String(error?.code || "");
      return (
        !statusCode ||
        errorCode === "ECONNABORTED" ||
        errorCode === "ERR_NETWORK" ||
        errorCode === "ERR_BAD_RESPONSE"
      );
    },
    async requestWithRetry(requestFn, { retries = 2, baseDelayMs = 850 } = {}) {
      let attempt = 0;
      let lastError = null;

      while (attempt <= retries) {
        try {
          return await requestFn();
        } catch (error) {
          lastError = error;
          if (!this.isRetryableApiError(error) || attempt === retries) {
            throw error;
          }
          const retryDelay = baseDelayMs * (attempt + 1);
          await this.wait(retryDelay);
          attempt += 1;
        }
      }

      throw lastError;
    },
    getCourseCatalogErrorMessage(error) {
      if (this.isRetryableApiError(error)) {
        return this.t("message.api.catalogUnavailable");
      }
      return this.t("message.api.catalogLoadFailed");
    },
    getCourseFetchErrorMessage(error, courseCode = "") {
      const statusCode = Number(error?.response?.status || 0);
      if (statusCode === 404) {
        return this.t("message.api.courseNotFound", {
          code: courseCode || "—",
        });
      }
      if (this.isRetryableApiError(error)) {
        return this.t("message.api.courseUnavailable");
      }
      return this.t("message.dashboard.fetchFailed");
    },
    async retryLoadCourses() {
      this.dashboardMessage = "";
      this.clearCourseCache();
      await this.fetchCourses({ cache: false });
    },
    isDevStudyPlanMockEnabled() {
      return !IS_PRODUCTION_BUILD && DEV_STUDY_PLAN_MOCK_ENABLED;
    },
    readDevStudyPlanCodes() {
      if (!this.isDevStudyPlanMockEnabled()) return [];
      try {
        const storedValue = localStorage.getItem(DEV_STUDY_PLAN_STORAGE_KEY);
        if (!storedValue) return [];
        const parsedValue = JSON.parse(storedValue);
        if (!Array.isArray(parsedValue)) return [];
        return [
          ...new Set(
            parsedValue
              .map((code) => this.normalizeCourseCode(code))
              .filter(Boolean)
          ),
        ];
      } catch (error) {
        console.warn("Could not read dev study plan fallback", error);
        return [];
      }
    },
    persistDevStudyPlanCodes(courseCodes) {
      if (!this.isDevStudyPlanMockEnabled()) return;
      const normalizedCodes = [
        ...new Set(
          courseCodes
            .map((code) => this.normalizeCourseCode(code))
            .filter(Boolean)
        ),
      ];
      this.studyPlanCourseCodes = normalizedCodes;
      try {
        localStorage.setItem(
          DEV_STUDY_PLAN_STORAGE_KEY,
          JSON.stringify(normalizedCodes)
        );
      } catch (error) {
        console.warn("Could not persist dev study plan fallback", error);
      }
    },
    addStudyPlanCodeDevMock(courseCode) {
      const normalizedCode = this.normalizeCourseCode(courseCode);
      if (!normalizedCode) return false;
      const nextCodes = [
        ...new Set([...this.readDevStudyPlanCodes(), normalizedCode]),
      ];
      this.persistDevStudyPlanCodes(nextCodes);
      return true;
    },
    removeStudyPlanCodeDevMock(courseCode) {
      const normalizedCode = this.normalizeCourseCode(courseCode);
      if (!normalizedCode) return false;
      const nextCodes = this.readDevStudyPlanCodes().filter(
        (code) => code !== normalizedCode
      );
      this.persistDevStudyPlanCodes(nextCodes);
      return true;
    },
    shouldFallbackToDevStudyPlan(error) {
      if (!this.isDevStudyPlanMockEnabled()) return false;
      const statusCode = Number(error?.response?.status || 0);
      if (!statusCode) return true;
      return statusCode === 403 || statusCode === 404 || statusCode >= 500;
    },
    isCourseOfferedBothSemesters(courseCard) {
      const rows = this.getCardRowsForChart(courseCard);
      const semesters = new Set(
        rows
          .map((row) => Number(row?.semester))
          .filter((value) => Number.isFinite(value))
      );
      return semesters.has(1) && semesters.has(3);
    },
    isCourseOfferedBothSemestersRecentByCode(courseCode) {
      const normalizedCode = this.normalizeCourseCode(courseCode);
      if (!normalizedCode) return false;
      const courseMatch = this.allCourses.find(
        (course) =>
          this.normalizeCourseCode(course?.course_code) === normalizedCode
      );
      return Boolean(courseMatch?.offered_both_semesters_recent);
    },
    async loadCourses() {
      if (this.maintenanceMode) return;
      let cachedCourses = null;
      let timestamp = null;
      try {
        cachedCourses = localStorage.getItem(CACHE_KEY);
        timestamp = localStorage.getItem(TIMESTAMP_KEY);
      } catch (error) {
        console.warn(
          "localStorage unavailable; fetching courses without cache",
          error
        );
        await this.fetchCourses({ cache: false });
        return;
      }
      const now = Date.now();
      try {
        const ts = Number(timestamp);
        if (cachedCourses && Number.isFinite(ts) && now - ts < CACHE_DURATION) {
          const decompressed = LZString.decompress(cachedCourses);
          if (typeof decompressed !== "string")
            throw new Error("Cache decompress failed");
          const parsedCourses = JSON.parse(decompressed);
          if (!Array.isArray(parsedCourses))
            throw new Error("Cache JSON invalid");
          this.allCourses = parsedCourses;
          this.courseCatalogError = "";
          return;
        }
      } catch (error) {
        console.warn("Course cache invalid; clearing and refetching", error);
        this.clearCourseCache();
      }
      await this.fetchCourses();
    },
    async fetchCourses({ cache = true } = {}) {
      if (this.maintenanceMode) return;
      this.isLoadingCourses = true;
      this.courseCatalogError = "";
      this.beginApiRequest();
      try {
        const response = await this.requestWithRetry(
          () =>
            ApiService.getCourses({
              recentOnly: true,
              includeLegacyArrays: false,
            }),
          { retries: 2, baseDelayMs: 900 }
        );
        const responseCourses = Array.isArray(response?.data?.courses)
          ? response.data.courses
          : null;
        const courses = responseCourses
          ? responseCourses.map((course) => ({
              course_code: course.course_code,
              course_name: course.course_name,
              offered_spring_recent: Boolean(course.offered_spring_recent),
              offered_fall_recent: Boolean(course.offered_fall_recent),
              offered_both_semesters_recent: Boolean(
                course.offered_both_semesters_recent
              ),
            }))
          : (response?.data?.course_codes || []).map((code, index) => ({
              course_code: code,
              course_name: response?.data?.course_names?.[index],
            }));
        this.allCourses = courses;
        if (cache) {
          try {
            localStorage.setItem(
              CACHE_KEY,
              LZString.compress(JSON.stringify(courses))
            );
            localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
          } catch (error) {
            console.warn(
              "Failed to write course cache; continuing without cache",
              error
            );
          }
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        this.courseCatalogError = this.getCourseCatalogErrorMessage(error);
      } finally {
        this.isLoadingCourses = false;
        this.endApiRequest();
      }
    },
    async loadStudyPlanCourses() {
      if (!this.canUseStudyPlanFeature) {
        this.studyPlanCourseCodes = [];
        return;
      }
      if (!this.state.authToken) {
        this.studyPlanCourseCodes = this.readDevStudyPlanCodes();
        return;
      }
      try {
        const response = await ApiService.getStudyPlanCourses();
        const responseCodes = Array.isArray(response?.data?.course_codes)
          ? response.data.course_codes
          : [];
        this.studyPlanCourseCodes = responseCodes
          .map((code) => this.normalizeCourseCode(code))
          .filter(Boolean);
      } catch (error) {
        console.warn("Could not load study plan courses", error);
        if (this.shouldFallbackToDevStudyPlan(error)) {
          this.studyPlanCourseCodes = this.readDevStudyPlanCodes();
          return;
        }
        this.studyPlanCourseCodes = [];
      }
    },
    isCourseInStudyPlan(courseCode) {
      const resolvedCode = this.resolveCourseCodeForLookup(courseCode);
      const normalizedCode = this.normalizeCourseCode(
        resolvedCode || courseCode
      );
      if (!normalizedCode) return false;
      return this.studyPlanCourseCodes.includes(normalizedCode);
    },
    async addCourseToStudyPlan(courseCode) {
      if (!this.canUseStudyPlanFeature) {
        this.dashboardMessage = this.t(
          "message.dashboard.studyPlanUnavailable"
        );
        return;
      }
      const resolvedCode = this.resolveCourseCodeForLookup(courseCode);
      const normalizedCode = this.normalizeCourseCode(
        resolvedCode || courseCode
      );
      if (!normalizedCode) return;
      if (this.studyPlanCourseCodes.includes(normalizedCode)) {
        this.dashboardMessage = this.t(
          "message.dashboard.studyPlanAlreadySaved",
          {
            code: normalizedCode,
          }
        );
        return;
      }
      if (!this.state.authToken) {
        if (!this.isDevStudyPlanMockEnabled()) {
          this.dashboardMessage = this.t(
            "message.dashboard.studyPlanLoginRequired"
          );
          return;
        }
        this.addStudyPlanCodeDevMock(normalizedCode);
        this.dashboardMessage = this.t("message.dashboard.studyPlanSavedDev", {
          code: normalizedCode,
        });
        return;
      }

      this.isSyncingStudyPlan = true;
      try {
        const response = await ApiService.addStudyPlanCourse(normalizedCode);
        const responseCodes = Array.isArray(response?.data?.course_codes)
          ? response.data.course_codes
          : [];
        if (responseCodes.length > 0) {
          this.studyPlanCourseCodes = responseCodes
            .map((code) => this.normalizeCourseCode(code))
            .filter(Boolean);
        } else {
          this.studyPlanCourseCodes = [
            ...this.studyPlanCourseCodes,
            normalizedCode,
          ];
        }

        const savedCode = response?.data?.course?.course_code || normalizedCode;
        this.dashboardMessage = this.t("message.dashboard.studyPlanSaved", {
          code: savedCode,
        });
      } catch (error) {
        const statusCode = Number(error?.response?.status || 0);
        if (statusCode === 401) {
          this.dashboardMessage = this.t(
            "message.dashboard.studyPlanLoginRequired"
          );
        } else if (this.shouldFallbackToDevStudyPlan(error)) {
          this.addStudyPlanCodeDevMock(normalizedCode);
          this.dashboardMessage = this.t(
            "message.dashboard.studyPlanSavedDev",
            {
              code: normalizedCode,
            }
          );
        } else {
          this.dashboardMessage = this.t("message.dashboard.studyPlanFailed");
        }
      } finally {
        this.isSyncingStudyPlan = false;
      }
    },
    async removeCourseFromStudyPlan(courseCode) {
      if (!this.canUseStudyPlanFeature) {
        this.dashboardMessage = this.t(
          "message.dashboard.studyPlanUnavailable"
        );
        return;
      }
      const resolvedCode = this.resolveCourseCodeForLookup(courseCode);
      const normalizedCode = this.normalizeCourseCode(
        resolvedCode || courseCode
      );
      if (!normalizedCode) return;
      if (!this.state.authToken) {
        if (!this.isDevStudyPlanMockEnabled()) {
          this.dashboardMessage = this.t(
            "message.dashboard.studyPlanLoginRequired"
          );
          return;
        }
        this.removeStudyPlanCodeDevMock(normalizedCode);
        this.dashboardMessage = this.t(
          "message.dashboard.studyPlanRemovedDev",
          {
            code: normalizedCode,
          }
        );
        return;
      }

      this.isSyncingStudyPlan = true;
      try {
        const response = await ApiService.removeStudyPlanCourse(normalizedCode);
        const responseCodes = Array.isArray(response?.data?.course_codes)
          ? response.data.course_codes
          : [];
        if (responseCodes.length > 0) {
          this.studyPlanCourseCodes = responseCodes
            .map((code) => this.normalizeCourseCode(code))
            .filter(Boolean);
        } else {
          this.studyPlanCourseCodes = this.studyPlanCourseCodes.filter(
            (code) => code !== normalizedCode
          );
        }

        const removedCode =
          response?.data?.course?.course_code || normalizedCode;
        this.dashboardMessage = this.t("message.dashboard.studyPlanRemoved", {
          code: removedCode,
        });
      } catch (error) {
        const statusCode = Number(error?.response?.status || 0);
        if (statusCode === 401) {
          this.dashboardMessage = this.t(
            "message.dashboard.studyPlanLoginRequired"
          );
        } else if (this.shouldFallbackToDevStudyPlan(error)) {
          this.removeStudyPlanCodeDevMock(normalizedCode);
          this.dashboardMessage = this.t(
            "message.dashboard.studyPlanRemovedDev",
            {
              code: normalizedCode,
            }
          );
        } else {
          this.dashboardMessage = this.t("message.dashboard.studyPlanFailed");
        }
      } finally {
        this.isSyncingStudyPlan = false;
      }
    },
    async toggleStudyPlanCourse(courseCode) {
      if (this.isCourseInStudyPlan(courseCode)) {
        await this.removeCourseFromStudyPlan(courseCode);
      } else {
        await this.addCourseToStudyPlan(courseCode);
      }
    },
    createCourseCard(courseData) {
      const programOfferings = Array.isArray(courseData?.program_offerings)
        ? courseData.program_offerings
        : [];
      return {
        id: this.nextCourseCardId++,
        course_code: courseData.course_code,
        course_name: courseData.course_name,
        aggregated_data: courseData.aggregated_data,
        program_offerings: programOfferings,
        mode: DEFAULT_CARD_MODE,
        selectedTermKey: null,
        selectedYear: null,
        selectedSemester: null,
      };
    },
    normalizeStudyProgramCode(value) {
      return String(value || "")
        .trim()
        .toUpperCase();
    },
    normalizeInstitutionCode(value) {
      return String(value || "")
        .trim()
        .toUpperCase();
    },
    ensureDashboardProgramFilterSelection() {
      const institutionCodes = new Set(
        this.dashboardProgramInstitutionOptions.map((option) =>
          this.normalizeInstitutionCode(option.value)
        )
      );
      const normalizedInstitutionCode = this.normalizeInstitutionCode(
        this.dashboardProgramFilterInstitutionCode
      );
      if (!institutionCodes.has(normalizedInstitutionCode)) {
        this.dashboardProgramFilterInstitutionCode =
          DASHBOARD_PROGRAM_FILTER_ALL;
      } else {
        this.dashboardProgramFilterInstitutionCode = normalizedInstitutionCode;
      }

      if (!this.canSelectDashboardStudyProgram) {
        this.dashboardProgramFilterStudyProgramCode =
          DASHBOARD_PROGRAM_FILTER_ALL;
        return;
      }

      const programCodes = new Set(
        this.dashboardProgramStudyProgramOptions.map((option) =>
          this.normalizeStudyProgramCode(option.value)
        )
      );
      const normalizedProgramCode = this.normalizeStudyProgramCode(
        this.dashboardProgramFilterStudyProgramCode
      );
      this.dashboardProgramFilterStudyProgramCode = programCodes.has(
        normalizedProgramCode
      )
        ? normalizedProgramCode
        : DASHBOARD_PROGRAM_FILTER_ALL;
    },
    refreshAllDistributionSelections() {
      this.selectedCourses.forEach((courseCard) => {
        if (courseCard?.mode !== "distribution") return;
        this.initializeCardDistributionSelection(courseCard);
      });
    },
    onDashboardProgramInstitutionChange() {
      this.ensureDashboardProgramFilterSelection();
      this.refreshAllDistributionSelections();
    },
    onDashboardProgramStudyProgramChange() {
      this.ensureDashboardProgramFilterSelection();
      this.refreshAllDistributionSelections();
    },
    getCardRowsForChart(courseCard) {
      const rows = this.getUnfilteredCardRows(courseCard);
      if (courseCard.mode === "distribution") return rows;
      return this.historyYears
        ? rows.filter(
            (row) =>
              row.year >= this.latestDashboardYear - this.historyYears + 1
          )
        : rows;
    },
    getUnfilteredCardRows(courseCard) {
      const fallbackRows = Array.isArray(courseCard?.aggregated_data)
        ? courseCard.aggregated_data
        : [];
      if (!this.isDashboardProgramFilterActive) {
        return this.sortRowsChronologically(fallbackRows);
      }
      if (!Array.isArray(courseCard?.program_offerings)) {
        return [];
      }
      const selectedInstitutionCode = this.normalizeInstitutionCode(
        this.dashboardProgramFilterInstitutionCode
      );
      const selectedProgramCode = this.normalizeStudyProgramCode(
        this.dashboardProgramFilterStudyProgramCode
      );
      const scopedRows = courseCard.program_offerings.filter((offering) => {
        const offeringInstitutionCode = this.normalizeInstitutionCode(
          offering?.institution_code
        );
        const offeringProgramCode = this.normalizeStudyProgramCode(
          offering?.study_program_code
        );
        if (
          selectedInstitutionCode !== DASHBOARD_PROGRAM_FILTER_ALL &&
          offeringInstitutionCode !== selectedInstitutionCode
        ) {
          return false;
        }
        if (
          selectedProgramCode !== DASHBOARD_PROGRAM_FILTER_ALL &&
          offeringProgramCode !== selectedProgramCode
        ) {
          return false;
        }
        return true;
      });
      if (!scopedRows.length) {
        return [];
      }
      return this.sortRowsChronologically(scopedRows);
    },
    setCardTermSelection(courseCard, termOption) {
      if (!termOption) return;
      courseCard.selectedTermKey = termOption.key;
      courseCard.selectedYear = termOption.year;
      courseCard.selectedSemester = termOption.semester;
    },
    initializeCardDistributionSelection(courseCard) {
      const termOptions = this.getCardTermOptions(courseCard);
      if (!termOptions.length) {
        courseCard.selectedTermKey = null;
        courseCard.selectedYear = null;
        courseCard.selectedSemester = null;
        return;
      }
      const selectedOption =
        termOptions.find(
          (option) => option.key === courseCard.selectedTermKey
        ) || termOptions[0];
      this.setCardTermSelection(courseCard, selectedOption);
    },
    getCardTermOptions(courseCard) {
      return this.buildTermOptions(this.getCardRowsForChart(courseCard));
    },
    getSelectedTermIndex(courseCard) {
      const options = this.getCardTermOptions(courseCard);
      return options.findIndex(
        (option) => option.key === courseCard.selectedTermKey
      );
    },
    canSelectOlderTerm(courseCard) {
      const selectedIndex = this.getSelectedTermIndex(courseCard);
      const options = this.getCardTermOptions(courseCard);
      return selectedIndex >= 0 && selectedIndex < options.length - 1;
    },
    canSelectNewerTerm(courseCard) {
      return this.getSelectedTermIndex(courseCard) > 0;
    },
    selectOlderTerm(courseCard) {
      if (!this.canSelectOlderTerm(courseCard)) return;
      const options = this.getCardTermOptions(courseCard);
      const targetIndex = this.getSelectedTermIndex(courseCard) + 1;
      this.setCardTermSelection(courseCard, options[targetIndex]);
    },
    selectNewerTerm(courseCard) {
      if (!this.canSelectNewerTerm(courseCard)) return;
      const options = this.getCardTermOptions(courseCard);
      const targetIndex = this.getSelectedTermIndex(courseCard) - 1;
      this.setCardTermSelection(courseCard, options[targetIndex]);
    },
    onCardTermSelectChange(courseCard) {
      const options = this.getCardTermOptions(courseCard);
      const selectedOption = options.find(
        (option) => option.key === courseCard.selectedTermKey
      );
      if (!selectedOption) return;
      this.setCardTermSelection(courseCard, selectedOption);
    },
    onCardModeChange(courseCard) {
      if (courseCard.mode === "distribution") {
        this.initializeCardDistributionSelection(courseCard);
      }
    },
    async fetchCourseStats(courseCode) {
      this.pendingCourseStatsRequests += 1;
      this.beginApiRequest();
      try {
        const [aggregatedResponse, programResponse] = await Promise.all([
          this.requestWithRetry(
            () => ApiService.getAggregatedCourseStats(courseCode),
            { retries: 2, baseDelayMs: 950 }
          ),
          this.requestWithRetry(
            () => ApiService.getCourseProgramStats(courseCode),
            { retries: 1, baseDelayMs: 700 }
          ).catch(() => null),
        ]);

        if (!aggregatedResponse?.data?.aggregated_data?.length) {
          throw new Error("Invalid course data");
        }

        const programOfferings = Array.isArray(programResponse?.data?.offerings)
          ? programResponse.data.offerings
          : [];
        return {
          ...aggregatedResponse.data,
          program_offerings: programOfferings,
        };
      } finally {
        this.pendingCourseStatsRequests = Math.max(
          0,
          this.pendingCourseStatsRequests - 1
        );
        this.endApiRequest();
      }
    },
    normalizeCourseCode(value) {
      return String(value || "")
        .trim()
        .toUpperCase();
    },
    clamp01(value) {
      if (!Number.isFinite(value)) return 0;
      return Math.max(0, Math.min(1, value));
    },
    formatPercent(value) {
      return `${Math.round(this.clamp01(Number(value)) * 100)}%`;
    },
    sortRowsByTerm(rows) {
      return [...rows].sort(
        (left, right) =>
          Number(right.year || 0) - Number(left.year || 0) ||
          Number(right.semester || 0) - Number(left.semester || 0)
      );
    },
    sortRowsChronologically(rows) {
      return [...rows].sort(
        (left, right) =>
          Number(left.year || 0) - Number(right.year || 0) ||
          Number(left.semester || 0) - Number(right.semester || 0)
      );
    },
    averageFromRows(rows, key) {
      const values = rows
        .map((row) => Number(row?.[key]))
        .filter((value) => Number.isFinite(value));
      if (!values.length) return null;
      const total = values.reduce((sum, value) => sum + value, 0);
      return total / values.length;
    },
    stdDev(values) {
      const numericValues = values.filter((value) => Number.isFinite(value));
      if (numericValues.length <= 1) return 0;
      const mean =
        numericValues.reduce((sum, value) => sum + value, 0) /
        numericValues.length;
      const variance =
        numericValues.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
        numericValues.length;
      return Math.sqrt(variance);
    },
    classifyDashboardQuadrant(riskScore, rewardScore) {
      if (riskScore <= 0.4 && rewardScore >= 0.6) return "smart_picks";
      if (riskScore <= 0.4 && rewardScore < 0.6) return "safe_choices";
      if (riskScore > 0.4 && rewardScore >= 0.6) return "high_risk_high_reward";
      return "review_carefully";
    },
    computeDashboardPriority({
      riskScore,
      rewardScore,
      offerProbability,
      stabilityScore,
      quadrant,
    }) {
      const profileTargets = {
        low: 0.25,
        balanced: 0.45,
        high: 0.65,
      };
      const targetRisk = profileTargets[this.dashboardRiskProfile] ?? 0.45;
      const riskFit = 1 - Math.abs(riskScore - targetRisk);
      const quadrantBonus = {
        smart_picks: 0.03,
        safe_choices: 0.01,
        high_risk_high_reward: 0.0,
        review_carefully: -0.03,
      }[quadrant];

      return (
        rewardScore * 0.52 +
        riskFit * 0.24 +
        offerProbability * 0.14 +
        stabilityScore * 0.1 +
        (quadrantBonus || 0)
      );
    },
    getAverageInsightMetric(metricKey) {
      const values = this.dashboardInsights
        .map((item) => Number(item?.[metricKey]))
        .filter((value) => Number.isFinite(value));
      if (!values.length) return null;
      return values.reduce((sum, value) => sum + value, 0) / values.length;
    },
    toMapPercent(value) {
      const clamped = this.clamp01(Number(value));
      return Math.round(clamped * 88 + 6);
    },
    toMapAxisPercent(percentValue) {
      const clamped = Math.max(0, Math.min(100, Number(percentValue)));
      return Math.round(clamped * 0.88 + 6);
    },
    getRiskRewardPointTitle(point) {
      const titleParts = [
        `${point.course_code} · ${point.course_name || ""}`.trim(),
        `${this.t("message.dashboard.riskAxisLabel")}: ${this.formatPercent(
          point.riskScore
        )}`,
        `${this.t("message.dashboard.rewardAxisLabel")}: ${this.formatPercent(
          point.rewardScore
        )}`,
        `${this.t("message.advanced.metrics.passRate")}: ${this.formatPercent(
          point.passRate
        )}`,
        `${this.t(
          "message.advanced.metrics.offerProbability"
        )}: ${this.formatPercent(point.offerProbability)}`,
        `${this.t("message.dashboard.signalStability")}: ${this.formatPercent(
          point.stabilityScore
        )}`,
      ];
      return titleParts.join("\n");
    },
    formatRiskRewardPointLabel(courseCode) {
      const normalizedCode = this.normalizeCourseCode(courseCode);
      if (!normalizedCode) return "—";
      const baseCode = normalizedCode.split("-")[0];
      if (baseCode.length <= 6) return baseCode;
      return `${baseCode.slice(0, 6)}…`;
    },
    handleRiskRewardPointClick(point) {
      if (!point?.course_code) return;
      this.focusDashboardCourseCard(point.course_code);
    },
    focusDashboardCourseCard(courseCode) {
      const normalizedCode = this.normalizeCourseCode(courseCode);
      if (!normalizedCode) return;
      const matchingCard = this.selectedCourses.find(
        (courseCard) =>
          this.normalizeCourseCode(courseCard?.course_code) === normalizedCode
      );
      if (!matchingCard) return;

      this.focusedCourseCardId = matchingCard.id;
      if (this.focusResetTimerId) {
        clearTimeout(this.focusResetTimerId);
        this.focusResetTimerId = null;
      }
      this.focusResetTimerId = setTimeout(() => {
        this.focusedCourseCardId = null;
        this.focusResetTimerId = null;
      }, 1700);

      this.$nextTick(() => {
        const targetElement = document.getElementById(
          `dashboard-course-card-${matchingCard.id}`
        );
        if (!targetElement) return;
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      });
    },
    buildDashboardInsight(courseCard) {
      const aggregatedData = this.getCardRowsForChart(courseCard);
      const validRows = aggregatedData.filter(
        (row) =>
          row &&
          Number.isFinite(Number(row.year)) &&
          Number.isFinite(Number(row.semester))
      );
      if (!validRows.length) {
        return null;
      }

      const sortedRows = this.sortRowsByTerm(validRows);
      const gradeKey = this.excludeFails
        ? "average_grade_no_fails"
        : "average_grade";
      const averageGradeRaw = this.averageFromRows(sortedRows, gradeKey);
      const averageGrade =
        averageGradeRaw == null
          ? this.averageFromRows(sortedRows, "average_grade") ?? 0
          : averageGradeRaw;
      const passRate = this.averageFromRows(sortedRows, "pass_rate") ?? 0;

      const uniqueYears = new Set(sortedRows.map((row) => Number(row.year)))
        .size;
      const uniqueTermPairs = new Set(
        sortedRows.map((row) => `${row.year}-${row.semester}`)
      ).size;
      const offerProbability = this.clamp01(
        uniqueTermPairs / Math.max(uniqueYears * 2, 1)
      );

      const gradeValues = sortedRows
        .map((row) => Number(row?.[gradeKey]))
        .filter((value) => Number.isFinite(value));
      const gradeSem = this.stdDev(gradeValues);
      const stabilityScore = this.clamp01(1 - gradeSem / 1.1);

      const rewardScore = this.clamp01(
        (averageGrade / 5) * 0.84 + stabilityScore * 0.16
      );
      const riskScore = this.clamp01(
        (1 - passRate) * 0.63 +
          (1 - offerProbability) * 0.22 +
          (1 - stabilityScore) * 0.15
      );
      const quadrant = this.classifyDashboardQuadrant(riskScore, rewardScore);
      const offeredSemesters = new Set(
        sortedRows.map((row) => Number(row.semester))
      );
      const offeredBothSemesters =
        offeredSemesters.has(1) && offeredSemesters.has(3);

      return {
        course_code: courseCard.course_code,
        course_name: courseCard.course_name,
        passRate,
        offerProbability,
        stabilityScore,
        riskScore,
        rewardScore,
        quadrant,
        offeredBothSemesters,
        priority: this.computeDashboardPriority({
          riskScore,
          rewardScore,
          offerProbability,
          stabilityScore,
          quadrant,
        }),
      };
    },
    setAllCardModes(mode) {
      this.comparisonTerm = "";
      if (!["historical", "distribution", "failRates"].includes(mode)) return;
      this.selectedCourses = this.selectedCourses.map((courseCard) => {
        const nextCard = { ...courseCard, mode };
        if (mode === "distribution") {
          this.initializeCardDistributionSelection(nextCard);
        }
        return nextCard;
      });
      if (
        mode === "distribution" &&
        this.selectedCourses.length > 1 &&
        this.commonTerms.length
      ) {
        this.comparisonTerm = this.commonTerms[0].key;
        this.applyComparisonTerm();
      }
    },
    isPaletteValid(value) {
      return Object.prototype.hasOwnProperty.call(CHART_PALETTES, value);
    },
    getActivePalette() {
      if (this.isPaletteValid(this.selectedPaletteKey)) {
        return CHART_PALETTES[this.selectedPaletteKey];
      }
      return CHART_PALETTES[DEFAULT_CHART_PALETTE];
    },
    getLineColor(lineKey) {
      const palette = this.getActivePalette();
      const fallbackPalette = CHART_PALETTES[DEFAULT_CHART_PALETTE];
      return (
        palette.lines[lineKey] || fallbackPalette.lines[lineKey] || "#2563eb"
      );
    },
    getExportTimestamp() {
      return new Date().toISOString().replace(/[:.]/g, "-");
    },
    downloadTextFile(content, fileName, mimeType) {
      const blob = new Blob([content], { type: mimeType });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    },
    escapeCsvValue(rawValue) {
      if (rawValue == null) return "";
      const value = String(rawValue);
      if (/[",\n]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    },
    exportDashboardCsv() {
      if (!this.isPremiumDashboardUnlocked) {
        this.dashboardMessage = this.t("message.dashboard.exportLocked");
        return;
      }
      if (!this.selectedCourses.length) return;

      const header = [
        "course_code",
        "course_name",
        "year",
        "semester",
        "average_grade",
        "average_grade_no_fails",
        "average_grade_women",
        "average_grade_women_no_fails",
        "pass_rate",
        "pass_rate_women",
        "total_students",
        "total_women",
        "grade_A",
        "grade_B",
        "grade_C",
        "grade_D",
        "grade_E",
        "grade_F",
      ];

      const rows = [header.join(",")];
      this.selectedCourses.forEach((courseCard) => {
        const aggregatedData = this.getCardRowsForChart(courseCard);
        aggregatedData.forEach((dataPoint) => {
          const gradeDistribution = dataPoint?.grade_distribution || {};
          const row = [
            courseCard.course_code,
            courseCard.course_name,
            dataPoint?.year,
            dataPoint?.semester,
            dataPoint?.average_grade,
            dataPoint?.average_grade_no_fails,
            dataPoint?.average_grade_women,
            dataPoint?.average_grade_women_no_fails,
            dataPoint?.pass_rate,
            dataPoint?.pass_rate_women,
            dataPoint?.total_students,
            dataPoint?.total_women,
            gradeDistribution.A,
            gradeDistribution.B,
            gradeDistribution.C,
            gradeDistribution.D,
            gradeDistribution.E,
            gradeDistribution.F,
          ];
          rows.push(row.map((value) => this.escapeCsvValue(value)).join(","));
        });
      });

      this.downloadTextFile(
        rows.join("\n"),
        `onlygrades-dashboard-${this.getExportTimestamp()}.csv`,
        "text/csv;charset=utf-8"
      );
      this.dashboardMessage = this.t("message.dashboard.exportReadyCsv");
    },
    exportDashboardJson() {
      if (!this.isPremiumDashboardUnlocked) {
        this.dashboardMessage = this.t("message.dashboard.exportLocked");
        return;
      }
      if (!this.selectedCourses.length) return;

      const payload = {
        generated_at: new Date().toISOString(),
        palette: this.selectedPaletteKey,
        exclude_fails: this.excludeFails,
        show_female_stats: this.showFemaleStats,
        program_filter: {
          institution_code: this.dashboardProgramFilterInstitutionCode,
          study_program_code: this.dashboardProgramFilterStudyProgramCode,
        },
        courses: this.selectedCourses.map((courseCard) => ({
          course_code: courseCard.course_code,
          course_name: courseCard.course_name,
          chart_mode: courseCard.mode,
          selected_term_key: courseCard.selectedTermKey,
          aggregated_data: this.getCardRowsForChart(courseCard),
        })),
      };

      this.downloadTextFile(
        JSON.stringify(payload, null, 2),
        `onlygrades-dashboard-${this.getExportTimestamp()}.json`,
        "application/json;charset=utf-8"
      );
      this.dashboardMessage = this.t("message.dashboard.exportReadyJson");
    },
    getDashboardStorageKey() {
      const userScope = this.state.user?.email
        ? String(this.state.user.email).trim().toLowerCase()
        : "guest";
      return `${DASHBOARD_STORAGE_KEY_PREFIX}:${userScope}`;
    },
    refreshSavedDashboardAvailability() {
      try {
        this.savedDashboardAvailable = Boolean(
          localStorage.getItem(this.getDashboardStorageKey())
        );
      } catch (error) {
        void error;
        this.savedDashboardAvailable = false;
      }
    },
    buildDashboardSnapshot() {
      return {
        version: 1,
        historyYears: this.historyYears,
        workspaceLayout: this.workspaceLayout,
        excludeFails: Boolean(this.excludeFails),
        showFemaleStats: Boolean(this.showFemaleStats),
        filterOfferedBothSemesters: Boolean(this.filterOfferedBothSemesters),
        selectedPaletteKey: this.selectedPaletteKey,
        dashboardRiskProfile: this.dashboardRiskProfile,
        dashboardProgramFilterInstitutionCode:
          this.dashboardProgramFilterInstitutionCode ||
          DASHBOARD_PROGRAM_FILTER_ALL,
        dashboardProgramFilterStudyProgramCode:
          this.dashboardProgramFilterStudyProgramCode ||
          DASHBOARD_PROGRAM_FILTER_ALL,
        isDashboardToolsExpanded: Boolean(this.isDashboardToolsExpanded),
        courses: this.selectedCourses.map((courseCard) => ({
          course_code: courseCard.course_code,
          mode: courseCard.mode,
          selectedTermKey: courseCard.selectedTermKey || null,
        })),
      };
    },
    persistDashboardState() {
      if (this.isRestoringDashboard || this.isCourseDetailView) return;
      try {
        const snapshot = JSON.stringify(this.buildDashboardSnapshot());
        localStorage.setItem(this.getDashboardStorageKey(), snapshot);
      } catch (error) {
        void error;
      }
      this.refreshSavedDashboardAvailability();
    },
    saveDashboardState() {
      this.persistDashboardState();
      this.dashboardMessage = this.t("message.dashboard.dashboardSaved");
    },
    applySnapshotToCourseCard(courseCard, snapshot) {
      const allowedModes = new Set(["historical", "distribution", "failRates"]);
      const requestedMode = String(snapshot?.mode || "").trim();
      courseCard.mode = allowedModes.has(requestedMode)
        ? requestedMode
        : DEFAULT_CARD_MODE;
      if (courseCard.mode !== "distribution") return;
      this.initializeCardDistributionSelection(courseCard);
      const savedTermKey = String(snapshot?.selectedTermKey || "").trim();
      if (!savedTermKey) return;
      const matchedOption = this.getCardTermOptions(courseCard).find(
        (option) => option.key === savedTermKey
      );
      if (matchedOption) {
        this.setCardTermSelection(courseCard, matchedOption);
      }
    },
    async restoreDashboardFromStorage(showMessage = false) {
      const generation = ++this.dashboardGeneration;
      const selectionRevision = this.selection.revision;
      this.isRestoringDashboard = false;
      let rawSnapshot = null;
      try {
        rawSnapshot = localStorage.getItem(this.getDashboardStorageKey());
      } catch (error) {
        void error;
      }

      if (!rawSnapshot && this.selection.initialized && !showMessage)
        rawSnapshot = JSON.stringify({
          courses: [],
          workspaceLayout: "combined",
          historyYears: 5,
          excludeFails: false,
        });
      if (!rawSnapshot) {
        if (showMessage) {
          this.dashboardMessage = this.t("message.dashboard.noSavedDashboard");
        }
        this.savedDashboardAvailable = false;
        return;
      }

      let snapshot = null;
      try {
        snapshot = JSON.parse(rawSnapshot);
      } catch (error) {
        void error;
        if (showMessage) {
          this.dashboardMessage = this.t(
            "message.dashboard.dashboardLoadFailed"
          );
        }
        return;
      }

      const savedCourses = Array.isArray(snapshot?.courses)
        ? snapshot.courses.slice(0, this.maxDashboardCharts)
        : [];
      const courseSnapshots =
        this.selection.initialized && !showMessage
          ? this.selection.courses
              .slice(0, this.maxDashboardCharts)
              .map((course) => ({
                ...savedCourses.find(
                  (saved) => saved.course_code === course.course_code
                ),
                course_code: course.course_code,
              }))
          : savedCourses;
      const restoredCards = [];

      this.isRestoringDashboard = true;
      try {
        this.workspaceLayout =
          snapshot?.workspaceLayout === "combined" ? "combined" : "cards";
        this.historyYears = [0, 5, 10].includes(snapshot?.historyYears)
          ? snapshot.historyYears
          : 0;
        this.excludeFails = snapshot?.excludeFails !== false;
        this.showFemaleStats = Boolean(snapshot?.showFemaleStats);
        this.filterOfferedBothSemesters = Boolean(
          snapshot?.filterOfferedBothSemesters
        );
        const savedPalette = String(
          snapshot?.selectedPaletteKey || DEFAULT_CHART_PALETTE
        );
        this.selectedPaletteKey = this.isPaletteValid(savedPalette)
          ? savedPalette
          : DEFAULT_CHART_PALETTE;
        const savedRiskProfile = String(
          snapshot?.dashboardRiskProfile || "balanced"
        );
        this.dashboardRiskProfile = ["low", "balanced", "high"].includes(
          savedRiskProfile
        )
          ? savedRiskProfile
          : "balanced";
        const savedProgramInstitutionCode = this.normalizeInstitutionCode(
          snapshot?.dashboardProgramFilterInstitutionCode ||
            DASHBOARD_PROGRAM_FILTER_ALL
        );
        const savedProgramStudyCode = this.normalizeStudyProgramCode(
          snapshot?.dashboardProgramFilterStudyProgramCode ||
            DASHBOARD_PROGRAM_FILTER_ALL
        );
        this.isDashboardToolsExpanded = Boolean(
          snapshot?.isDashboardToolsExpanded
        );

        for (const item of courseSnapshots) {
          if (generation !== this.dashboardGeneration) return;
          const normalizedCode = this.normalizeCourseCode(item?.course_code);
          if (!normalizedCode) continue;
          try {
            // eslint-disable-next-line no-await-in-loop
            const courseData = await this.fetchCourseStats(normalizedCode);
            if (generation !== this.dashboardGeneration) return;
            const courseCard = this.createCourseCard(courseData);
            this.applySnapshotToCourseCard(courseCard, item);
            restoredCards.push(courseCard);
          } catch (error) {
            console.warn(
              "Skipping saved course during restore",
              normalizedCode,
              error
            );
          }
        }

        if (generation !== this.dashboardGeneration) return;
        this.selectedCourses = restoredCards;
        this.initialSearchDone = restoredCards.length > 0;
        this.dashboardProgramFilterInstitutionCode =
          savedProgramInstitutionCode;
        this.dashboardProgramFilterStudyProgramCode = savedProgramStudyCode;
        this.ensureDashboardProgramFilterSelection();
        this.refreshAllDistributionSelections();
        if (restoredCards.length) {
          this.initialSearchDone = true;
          if (showMessage) {
            this.dashboardMessage = this.t("message.dashboard.dashboardLoaded");
          }
        } else if (showMessage) {
          this.dashboardMessage = this.t("message.dashboard.noSavedDashboard");
        }
      } finally {
        if (generation === this.dashboardGeneration) {
          this.isRestoringDashboard = false;
          this.refreshSavedDashboardAvailability();
          if (selectionRevision === this.selection.revision) {
            // A failed read must not silently remove a shortlisted course.
            if (
              !this.selection.initialized ||
              restoredCards.length === courseSnapshots.length
            )
              this.commitSharedSelection();
          } else this.syncFromSharedSelection();
        }
      }
    },
    hasRouteCourseSelection(routeLike) {
      return this.parseRouteCourseCodes(routeLike).length > 0;
    },
    parseRouteCourseCodes(routeLike) {
      const collectedCodes = [];
      const pushCodes = (rawValue) => {
        if (rawValue == null) return;
        const values = Array.isArray(rawValue)
          ? rawValue
          : String(rawValue).split(/[,\s]+/);
        values.forEach((value) => {
          const normalized = String(value || "")
            .trim()
            .toUpperCase();
          if (normalized && !collectedCodes.includes(normalized)) {
            collectedCodes.push(normalized);
          }
        });
      };

      pushCodes(routeLike?.params?.courseCode);
      pushCodes(routeLike?.query?.courses);
      pushCodes(routeLike?.query?.compare);
      return collectedCodes;
    },
    async applyRouteCourseSelection(routeLike) {
      const courseCodes = this.parseRouteCourseCodes(routeLike);
      if (!courseCodes.length) return;
      if (
        courseCodes.join(",") ===
        this.selectedCourses.map((course) => course.course_code).join(",")
      )
        return;

      const generation = ++this.dashboardGeneration;
      this.isRestoringDashboard = false;
      this.initialSearchDone = true;
      this.dashboardMessage = "";
      this.selectedCourses = [];
      if (routeLike?.query?.source === "explore") this.historyYears = 5;

      for (const courseCode of courseCodes) {
        let added;
        try {
          added = await this.addCourseByCode(courseCode, {
            duplicateMessage: false,
            limitMessage: false,
          });
        } catch (error) {
          if (generation !== this.dashboardGeneration) return;
          this.dashboardMessage = this.getCourseFetchErrorMessage(
            error,
            courseCode
          );
          continue;
        }
        if (generation !== this.dashboardGeneration) return;
        if (!added && this.selectedCourses.length >= this.maxDashboardCharts) {
          this.dashboardMessage =
            !this.state.features.premiumEnabled ||
            this.isPremiumDashboardUnlocked
              ? this.t("message.dashboard.limitReached", {
                  max: this.maxDashboardCharts,
                })
              : this.t("message.dashboard.premiumLocked");
          break;
        }
      }
      if (
        generation === this.dashboardGeneration &&
        this.selectedCourses.length ===
          Math.min(courseCodes.length, this.maxDashboardCharts)
      )
        this.commitSharedSelection(routeLike.query);
      if (
        generation === this.dashboardGeneration &&
        routeLike?.query?.layout === "combined" &&
        this.selectedCourses.length > 1
      ) {
        this.excludeFails = false;
        this.setAllCardModes("distribution");
        this.setWorkspaceLayout("combined");
      }
    },
    async addCourseByCode(
      courseCode,
      { duplicateMessage = true, limitMessage = true } = {}
    ) {
      const normalizedCode = String(courseCode || "")
        .trim()
        .toUpperCase();
      if (!normalizedCode) return false;

      const existingIndex = this.selectedCourses.findIndex(
        (courseCard) => courseCard.course_code.toUpperCase() === normalizedCode
      );

      if (existingIndex < 0 && !this.canAddCourse) {
        if (limitMessage) {
          this.dashboardMessage =
            !this.state.features.premiumEnabled ||
            this.isPremiumDashboardUnlocked
              ? this.t("message.dashboard.limitReached", {
                  max: this.maxDashboardCharts,
                })
              : this.t("message.dashboard.premiumLocked");
        }
        return false;
      }

      if (existingIndex >= 0) {
        if (duplicateMessage)
          this.dashboardMessage = this.t("message.dashboard.duplicateCourse");
        return true;
      }
      const generation = this.dashboardGeneration;
      const courseData = await this.fetchCourseStats(normalizedCode);
      if (generation !== this.dashboardGeneration) return false;
      // Other requests may have completed while this one was in flight.
      if (
        this.selectedCourses.some(
          (card) => card.course_code.toUpperCase() === normalizedCode
        )
      )
        return true;
      if (!this.canAddCourse) {
        if (limitMessage)
          this.dashboardMessage = this.t("message.dashboard.limitReached", {
            max: this.maxDashboardCharts,
          });
        return false;
      }
      const nextCard = this.createCourseCard(courseData);
      if (this.workspaceLayout === "combined" && this.selectedCourses.length)
        nextCard.mode = this.selectedCourses[0].mode;
      this.selectedCourses.push(nextCard);
      if (nextCard.mode === "distribution")
        this.initializeCardDistributionSelection(nextCard);
      if (
        this.workspaceLayout === "combined" &&
        this.selectedCourses.length > 1
      )
        this.setAllCardModes(nextCard.mode);
      return true;
    },
    resolveCourseCodeForLookup(courseCode) {
      const normalizedCode = this.normalizeCourseCode(courseCode);
      if (!normalizedCode) return "";

      const exactCourse = this.allCourses.find(
        (course) =>
          this.normalizeCourseCode(course.course_code) === normalizedCode
      );
      if (exactCourse?.course_code) {
        return exactCourse.course_code;
      }

      const suffixedMatches = this.allCourses
        .filter((course) =>
          this.normalizeCourseCode(course.course_code).startsWith(
            `${normalizedCode}-`
          )
        )
        .sort((left, right) =>
          this.normalizeCourseCode(left.course_code).localeCompare(
            this.normalizeCourseCode(right.course_code)
          )
        );

      if (suffixedMatches.length > 0) {
        return suffixedMatches[0].course_code;
      }

      return normalizedCode;
    },
    async handleSearch(courseObject) {
      if (this.maintenanceMode) return;
      if (this.isCourseCatalogUnavailable) {
        this.dashboardMessage = this.t("message.api.catalogUnavailable");
        return;
      }
      if (!courseObject?.course_code) return;
      if (this.isRestoringDashboard) {
        this.dashboardGeneration++;
        this.isRestoringDashboard = false;
      }

      const resolvedCourseCode = this.resolveCourseCodeForLookup(
        courseObject.course_code
      );
      if (!resolvedCourseCode) return;
      if (
        this.filterOfferedBothSemesters &&
        this.hasCourseOfferingMetadata &&
        !this.isCourseOfferedBothSemestersRecentByCode(resolvedCourseCode)
      ) {
        this.dashboardMessage = this.t(
          "message.dashboard.filterBothSemestersHelp"
        );
        return;
      }

      if (!this.initialSearchDone) {
        this.initialSearchDone = true;
      }
      this.dashboardMessage = "";

      try {
        await this.addCourseByCode(resolvedCourseCode);
        this.commitSharedSelection(null, true);
        this.syncSelectionToRoute();
      } catch (error) {
        console.error("Error fetching course stats:", error);
        this.dashboardMessage = this.getCourseFetchErrorMessage(
          error,
          resolvedCourseCode
        );
      }
    },
    removeCourseCard(courseCardId) {
      if (this.isRestoringDashboard) {
        this.dashboardGeneration++;
        this.isRestoringDashboard = false;
      }
      const removedCourse = this.selectedCourses.find(
        (course) => course.id === courseCardId
      );
      this.selectedCourses = this.selectedCourses.filter(
        (courseCard) => courseCard.id !== courseCardId
      );
      if (!this.selectedCourses.length) {
        this.dashboardMessage = "";
        this.initialSearchDone = false;
      }
      if (removedCourse && !this.isCourseDetailView)
        courseSelection.replaceCourses(
          this.selection.courses.filter(
            (course) => course.course_code !== removedCourse.course_code
          )
        );
      this.syncSelectionToRoute();
    },
    syncSelectionToRoute() {
      if (this.isCourseDetailView) return;
      if (!this.hasRouteCourseSelection(this.$route)) return;
      const query = { ...this.$route.query };
      if (this.selection.institution)
        query.institution = this.selection.institution;
      else delete query.institution;
      if (this.selection.program) query.program = this.selection.program;
      else delete query.program;
      delete query.compare;
      if (this.selectedCourses.length > 1) query.layout = this.workspaceLayout;
      else delete query.layout;
      if (this.selectedCourses.length)
        query.courses = this.selectedCourses
          .map((course) => course.course_code)
          .join(",");
      else {
        delete query.courses;
        delete query.layout;
      }
      this.$router.replace({ name: "home", query });
    },
    clearDashboardCourses() {
      this.dashboardGeneration++;
      this.isRestoringDashboard = false;
      this.selectedCourses = [];
      this.dashboardMessage = "";
      this.initialSearchDone = false;
      if (!this.isCourseDetailView) courseSelection.clear();
      this.syncSelectionToRoute();
    },
    getSemesterDefinitions() {
      return [
        { text: "winter", value: 0 },
        { text: "spring", value: 1 },
        { text: "summer", value: 2 },
        { text: "fall", value: 3 },
      ];
    },
    buildTermOptions(aggregatedData) {
      if (!Array.isArray(aggregatedData)) return [];
      const semesterTextByValue = this.getSemesterDefinitions().reduce(
        (lookup, semester) => {
          lookup[semester.value] = semester.text;
          return lookup;
        },
        {}
      );
      const uniqueMap = new Map();

      aggregatedData.forEach((dataPoint) => {
        if (
          !Number.isFinite(dataPoint?.year) ||
          !Number.isInteger(dataPoint?.semester)
        ) {
          return;
        }
        const semesterText = semesterTextByValue[dataPoint.semester];
        if (!semesterText) return;
        const key = `${dataPoint.year}-${dataPoint.semester}`;
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, {
            key,
            year: dataPoint.year,
            semester: dataPoint.semester,
            label: `${dataPoint.year} · ${this.t(
              `message.semesters.${semesterText}`
            )}`,
          });
        }
      });

      return [...uniqueMap.values()].sort(
        (left, right) =>
          right.year - left.year || right.semester - left.semester
      );
    },
    shouldUseSteppedSeries(aggregatedData) {
      if (!Array.isArray(aggregatedData) || aggregatedData.length === 0) {
        return false;
      }
      const validRows = aggregatedData.filter(
        (row) =>
          row &&
          Number.isFinite(Number(row.year)) &&
          Number.isFinite(Number(row.semester))
      );
      if (validRows.length <= 2) {
        return true;
      }
      const yearKeys = new Set(validRows.map((row) => String(row.year)));
      const termKeys = new Set(
        validRows.map((row) => `${row.year}-${row.semester}`)
      );
      return yearKeys.size >= 3 && termKeys.size === yearKeys.size;
    },
    getHistoricalViewData(aggregatedData) {
      const timelineRows = alignTerms(aggregatedData, this.timelineTerms);
      const semesterLabels = [
        this.t("message.semesters.winter"),
        this.t("message.semesters.spring"),
        this.t("message.semesters.summer"),
        this.t("message.semesters.fall"),
      ];
      const useSteppedSeries = false;
      const pointRadius = timelineRows.length < 12 ? 3 : 1.5;
      const averageGradeKey = this.excludeFails
        ? "average_grade_no_fails"
        : "average_grade";
      const averageGradeWomenKey = this.excludeFails
        ? "average_grade_women_no_fails"
        : "average_grade_women";
      const historicalColor = this.getLineColor("historical");
      const historicalWomenColor = this.getLineColor("historicalFemale");
      const datasets = [
        {
          label: this.t("message.chartHistorical"),
          data: timelineRows.map((d) => averageForRow(d, averageGradeKey)),
          backgroundColor: this.withAlpha(historicalColor, 0.18),
          borderColor: historicalColor,
          borderWidth: 3,
          fill: false,
          spanGaps: false,
          stepped: useSteppedSeries ? "before" : false,
          pointRadius,
          pointHoverRadius: useSteppedSeries ? 6 : 5,
          pointHitRadius: useSteppedSeries ? 18 : 14,
          tension: 0,
          type: "line",
        },
      ];

      if (this.showFemaleStats) {
        datasets.push({
          label:
            this.t("message.chartHistorical") +
            ` (${this.t("message.womenStats")})`,
          data: timelineRows.map((d) => averageForRow(d, averageGradeWomenKey)),
          backgroundColor: this.withAlpha(historicalWomenColor, 0.14),
          borderColor: historicalWomenColor,
          borderDash: [5, 4],
          borderWidth: 3,
          fill: false,
          spanGaps: false,
          stepped: useSteppedSeries ? "before" : false,
          pointRadius,
          pointHoverRadius: useSteppedSeries ? 6 : 5,
          pointHitRadius: useSteppedSeries ? 18 : 14,
          tension: 0,
          type: "line",
        });
      }

      return {
        labels: timelineRows.map(
          (d) => `${d.year} - ${semesterLabels[d.semester]}`
        ),
        datasets,
      };
    },
    getDistributionViewData(aggregatedData, year, semester) {
      const row = aggregatedData.find(
        (d) => d.year == year && d.semester == semester
      );
      const base = distributionForRow(row);
      if (!base) return { labels: [], datasets: [] };
      const labels = base.passFail
        ? [this.ui("Bestått", "Pass"), this.ui("Stryk", "Fail")]
        : base.labels;
      const colors = this.generateColors(labels.length);
      const datasets = [
        {
          label: this.ui("Alle vurderinger", "All results"),
          data: base.percentages,
          rawCounts: base.counts,
          backgroundColor: colors.borders,
          borderWidth: 0,
          borderRadius: 4,
          maxBarThickness: 44,
        },
      ];
      const women = this.showFemaleStats ? distributionForRow(row, true) : null;
      if (women && women.passFail === base.passFail) {
        const womenColors = this.generateColors(labels.length, "female");
        datasets.push({
          label: this.t("message.womenStats"),
          data: women.percentages,
          rawCounts: women.counts,
          backgroundColor: womenColors.fills,
          borderColor: womenColors.borders,
          borderWidth: 1.5,
          borderRadius: 4,
          maxBarThickness: 44,
        });
      }
      return { labels, datasets };
    },
    getFailRatesViewData(aggregatedData) {
      const timelineRows = alignTerms(aggregatedData, this.timelineTerms);
      const semesterLabels = [
        this.t("message.semesters.winter"),
        this.t("message.semesters.spring"),
        this.t("message.semesters.summer"),
        this.t("message.semesters.fall"),
      ];
      const useSteppedSeries = false;
      const pointRadius = timelineRows.length < 12 ? 3 : 1.5;
      const failRateColor = this.getLineColor("failRates");
      const failRateWomenColor = this.getLineColor("failRatesFemale");
      const datasets = [
        {
          label: this.t("message.chartFailRates"),
          data: timelineRows.map((d) => {
            const passRate = rateOrNull(d?.pass_rate);
            return passRate == null ? null : 1 - passRate;
          }),
          backgroundColor: this.withAlpha(failRateColor, 0.18),
          borderColor: failRateColor,
          borderWidth: 3,
          fill: false,
          spanGaps: false,
          stepped: useSteppedSeries ? "before" : false,
          pointRadius,
          pointHoverRadius: useSteppedSeries ? 6 : 5,
          pointHitRadius: useSteppedSeries ? 18 : 14,
          tension: 0,
          type: "line",
        },
      ];

      if (this.showFemaleStats) {
        datasets.push({
          label:
            this.t("message.chartFailRates") +
            ` (${this.t("message.womenStats")})`,
          data: timelineRows.map((d) => {
            const passRateWomen = rateOrNull(d?.pass_rate_women);
            return passRateWomen == null ? null : 1 - passRateWomen;
          }),
          backgroundColor: this.withAlpha(failRateWomenColor, 0.14),
          borderColor: failRateWomenColor,
          borderDash: [5, 4],
          borderWidth: 3,
          fill: false,
          spanGaps: false,
          stepped: useSteppedSeries ? "before" : false,
          pointRadius,
          pointHoverRadius: useSteppedSeries ? 6 : 5,
          pointHitRadius: useSteppedSeries ? 18 : 14,
          tension: 0,
          type: "line",
        });
      }

      return {
        labels: timelineRows.map(
          (d) => `${d.year} - ${semesterLabels[d.semester]}`
        ),
        datasets,
      };
    },
    withAlpha(hexColor, alpha) {
      const normalized = hexColor.replace("#", "");
      const safeHex =
        normalized.length === 3
          ? normalized
              .split("")
              .map((char) => `${char}${char}`)
              .join("")
          : normalized;
      const numericValue = parseInt(safeHex, 16);
      const red = (numericValue >> 16) & 255;
      const green = (numericValue >> 8) & 255;
      const blue = numericValue & 255;
      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },
    generateColors(count, variant = "base") {
      const palette = this.getActivePalette();
      const colorStops =
        variant === "female" ? palette.femaleBars : palette.baseBars;
      const fills = [];
      const borders = [];
      for (let i = 0; i < count; i++) {
        const currentColor = colorStops[i % colorStops.length];
        fills.push(
          this.withAlpha(currentColor, variant === "female" ? 0.48 : 0.66)
        );
        borders.push(this.withAlpha(currentColor, 1));
      }
      return { fills, borders };
    },
    getChartDataForCard(courseCard) {
      const rows = this.getCardRowsForChart(courseCard);
      if (hasAmbiguousTerms(rows, courseCard.mode, courseCard.selectedTermKey))
        return { labels: [], datasets: [] };
      const chart = this.getRawChartDataForCard(courseCard);
      chart.periods = this.timelineTerms;
      const aligned = alignTerms(rows, this.timelineTerms);
      const row = rows.find(
        (row) => termKey(row) === courseCard.selectedTermKey
      );
      chart.datasets.forEach((dataset, i) => {
        dataset.sampleCounts =
          courseCard.mode === "distribution"
            ? dataset.data.map(() =>
                metricSampleSize(row, "distribution", this.excludeFails, i > 0)
              )
            : aligned.map((row) =>
                metricSampleSize(row, courseCard.mode, this.excludeFails, i > 0)
              );
        if (courseCard.mode !== "distribution") {
          dataset.pointStyle = dataset.sampleCounts.map((count) =>
            ["small", "very-small"].includes(sampleLevel(count))
              ? "triangle"
              : "circle"
          );
          dataset.pointRadius = dataset.sampleCounts.map((count) =>
            ["small", "very-small"].includes(sampleLevel(count)) ? 4 : 1.5
          );
        }
      });
      return chart;
    },
    getRawChartDataForCard(courseCard) {
      const aggregatedData = this.getCardRowsForChart(courseCard);
      if (!aggregatedData.length) {
        return { labels: [], datasets: [] };
      }
      try {
        switch (courseCard.mode) {
          case "historical":
            return this.getHistoricalViewData(aggregatedData);
          case "distribution":
            return this.getDistributionViewData(
              aggregatedData,
              courseCard.selectedYear,
              courseCard.selectedSemester
            );
          case "failRates":
            return this.getFailRatesViewData(aggregatedData);
          default:
            return this.getHistoricalViewData(aggregatedData);
        }
      } catch (error) {
        console.error("Error computing chart data:", error);
        return { labels: [], datasets: [] };
      }
    },
    hasChartData(courseCard) {
      const chartData = this.getChartDataForCard(courseCard);
      return Array.isArray(chartData.datasets) && chartData.datasets.length > 0;
    },
    getChartOptionsForMode(mode) {
      const isDistribution = mode === "distribution";
      const legendDisplay = this.showFemaleStats;
      const sharedTicks = {
        color: "#6b7280",
        font: {
          family: "'Work Sans', sans-serif",
          size: 12,
          weight: "500",
        },
      };
      const sharedGrid = {
        color: "rgba(94, 120, 81, 0.13)",
        drawBorder: false,
        tickLength: 0,
      };
      const scales =
        mode === "historical"
          ? {
              x: {
                ticks: {
                  ...sharedTicks,
                  maxRotation: 0,
                  autoSkip: true,
                  maxTicksLimit: 6,
                },
                grid: { display: false },
              },
              y: {
                beginAtZero: true,
                min: 0,
                max: 5,
                grid: {
                  ...sharedGrid,
                  color: "rgba(94, 120, 81, 0.13)",
                },
                ticks: {
                  ...sharedTicks,
                  callback: function (value) {
                    if (Number.isInteger(value)) {
                      const gradeLabels = ["F", "E", "D", "C", "B", "A"];
                      return gradeLabels[value] + " (" + value + ")";
                    }
                    return null;
                  },
                  stepSize: 1,
                },
              },
            }
          : mode === "failRates"
          ? {
              x: {
                ticks: {
                  ...sharedTicks,
                  maxRotation: 0,
                  autoSkip: true,
                  maxTicksLimit: 6,
                },
                grid: { display: false },
              },
              y: {
                beginAtZero: true,
                max: 1,
                grid: {
                  ...sharedGrid,
                  color: "rgba(94, 120, 81, 0.13)",
                },
                ticks: {
                  ...sharedTicks,
                  callback: function (value) {
                    return (value * 100).toFixed(0) + "%";
                  },
                },
              },
            }
          : {
              x: {
                ticks: {
                  ...sharedTicks,
                  maxRotation: 0,
                },
                grid: {
                  display: false,
                },
              },
              y: {
                beginAtZero: true,
                max: 100,
                ticks: {
                  ...sharedTicks,
                  callback: (value) => `${value}%`,
                  stepSize: 25,
                },
                grid: { display: false },
              },
            };

      return {
        scales,
        animation: {
          duration: 180,
          easing: "linear",
        },
        elements: {
          line: {
            borderWidth: 3,
            tension: 0,
          },
          point: {
            radius: 0,
            hoverRadius: 5,
            hitRadius: 14,
          },
          bar: {
            borderRadius: 10,
            borderSkipped: false,
          },
        },
        plugins: {
          tooltip: {
            mode: isDistribution ? "nearest" : "index",
            intersect: false,
            backgroundColor: "#213f30",
            titleColor: "#f9fafb",
            bodyColor: "#e5e7eb",
            borderColor: "#374151",
            borderWidth: 1,
            cornerRadius: 12,
            padding: 12,
            titleFont: {
              family: "'Work Sans', sans-serif",
              size: 12,
              weight: "600",
            },
            bodyFont: {
              family: "'Work Sans', sans-serif",
              size: 12,
              weight: "500",
            },
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || "";
                const value = numberOrNull(context.raw);
                if (value == null) return `${label}: —`;
                const count = context.dataset.sampleCounts?.[context.dataIndex];
                const sample = count == null ? "" : ` · n=${Math.round(count)}`;
                if (mode === "failRates")
                  return `${label}: ${(value * 100).toFixed(1)}%${sample}`;
                if (mode === "distribution")
                  return `${label}: ${value.toFixed(1)}% · ${Math.round(
                    context.dataset.rawCounts?.[context.dataIndex] ?? 0
                  )}${sample}`;
                return `${label}: ${value.toFixed(2)} / 5${sample}`;
              },
            },
          },
          legend: {
            display: legendDisplay,
            position: "top",
            align: "start",
            labels: {
              color: "#374151",
              usePointStyle: true,
              pointStyle: "circle",
              boxWidth: 10,
              boxHeight: 10,
              padding: 16,
              font: {
                family: "'Work Sans', sans-serif",
                size: 12,
                weight: "600",
              },
            },
          },
        },
        interaction: {
          mode: isDistribution ? "nearest" : "index",
          axis: "x",
          intersect: false,
        },
      };
    },
    getChartTypeForMode(mode) {
      return mode === "distribution" ? "bar" : "line";
    },
  },
  computed: {
    isCourseDetailView() {
      return Boolean(this.$route.params?.courseCode);
    },
    isCombinedView() {
      return (
        this.workspaceLayout === "combined" &&
        this.visibleSelectedCourses.length > 1
      );
    },
    comparisonItems() {
      return this.visibleSelectedCourses.map((course) => ({
        course,
        rows: this.getCardRowsForChart(course),
        terms: this.getCardTermOptions(course),
        chartData: this.getChartDataForCard(course),
      }));
    },

    workspaceModes() {
      return [
        { value: "distribution", label: this.ui("Fordeling", "Distribution") },
        { value: "historical", label: this.ui("Utvikling", "Trend") },
        { value: "failRates", label: this.ui("Stryk", "Failure rate") },
      ];
    },
    latestDashboardYear() {
      return Math.max(
        0,
        ...this.selectedCourses.flatMap((card) =>
          this.getUnfilteredCardRows(card).map((row) => Number(row.year))
        )
      );
    },
    timelineTerms() {
      const terms = new Map();
      this.visibleSelectedCourses.forEach((card) =>
        this.getCardRowsForChart(card).forEach((row) =>
          terms.set(termKey(row), { year: row.year, semester: row.semester })
        )
      );
      return sortTerms([...terms.values()]);
    },
    commonTerms() {
      if (!this.selectedCourses.length) return [];
      return this.getCardTermOptions(this.selectedCourses[0]).filter((term) =>
        this.selectedCourses.every((card) =>
          this.getCardTermOptions(card).some(
            (option) => option.key === term.key
          )
        )
      );
    },

    showHero() {
      return !this.initialSearchDone;
    },
    isCourseCatalogUnavailable() {
      return this.isLoadingCourses || Boolean(this.courseCatalogError);
    },
    isFetchingCourseStats() {
      return this.pendingCourseStatsRequests > 0;
    },
    courseSelected() {
      return this.selectedCourses.length > 0;
    },
    chartModeOptions() {
      return [
        {
          value: "historical",
          label: this.t("message.chartHistorical"),
        },
        {
          value: "distribution",
          label: this.t("message.chartDistribution"),
        },
        {
          value: "failRates",
          label: this.t("message.chartFailRates"),
        },
      ];
    },
    isDashboardInstitutionFilterActive() {
      return (
        this.normalizeInstitutionCode(
          this.dashboardProgramFilterInstitutionCode
        ) !== DASHBOARD_PROGRAM_FILTER_ALL
      );
    },
    isDashboardStudyProgramFilterActive() {
      return (
        this.normalizeStudyProgramCode(
          this.dashboardProgramFilterStudyProgramCode
        ) !== DASHBOARD_PROGRAM_FILTER_ALL
      );
    },
    isDashboardProgramFilterActive() {
      return (
        this.isDashboardInstitutionFilterActive ||
        this.isDashboardStudyProgramFilterActive
      );
    },
    dashboardProgramInstitutionOptions() {
      const institutionsByCode = new Map();
      this.selectedCourses.forEach((courseCard) => {
        const offerings = Array.isArray(courseCard?.program_offerings)
          ? courseCard.program_offerings
          : [];
        offerings.forEach((offering) => {
          const institutionCode = this.normalizeInstitutionCode(
            offering?.institution_code
          );
          if (!institutionCode) return;
          const institutionName = String(
            offering?.institution_name || ""
          ).trim();
          if (!institutionsByCode.has(institutionCode)) {
            institutionsByCode.set(institutionCode, {
              value: institutionCode,
              label: institutionName
                ? `${institutionCode} · ${institutionName}`
                : institutionCode,
            });
          }
        });
      });

      const sortedInstitutions = [...institutionsByCode.values()].sort(
        (left, right) =>
          left.label.localeCompare(right.label, undefined, {
            sensitivity: "base",
          })
      );
      return [
        {
          value: DASHBOARD_PROGRAM_FILTER_ALL,
          label: this.t("message.dashboard.programFocusInstitutionAll"),
        },
        ...sortedInstitutions,
      ];
    },
    dashboardProgramStudyProgramOptions() {
      const selectedInstitutionCode = this.normalizeInstitutionCode(
        this.dashboardProgramFilterInstitutionCode
      );
      const studyProgramsByCode = new Map();

      this.selectedCourses.forEach((courseCard) => {
        const offerings = Array.isArray(courseCard?.program_offerings)
          ? courseCard.program_offerings
          : [];
        offerings.forEach((offering) => {
          const institutionCode = this.normalizeInstitutionCode(
            offering?.institution_code
          );
          if (
            selectedInstitutionCode !== DASHBOARD_PROGRAM_FILTER_ALL &&
            institutionCode !== selectedInstitutionCode
          ) {
            return;
          }
          const studyProgramCode = this.normalizeStudyProgramCode(
            offering?.study_program_code
          );
          if (!studyProgramCode) return;
          const studyProgramName = String(
            offering?.study_program_name || ""
          ).trim();
          if (!studyProgramsByCode.has(studyProgramCode)) {
            studyProgramsByCode.set(studyProgramCode, {
              value: studyProgramCode,
              label: studyProgramName
                ? `${studyProgramCode} · ${studyProgramName}`
                : studyProgramCode,
            });
          }
        });
      });

      const sortedPrograms = [...studyProgramsByCode.values()].sort(
        (left, right) =>
          left.label.localeCompare(right.label, undefined, {
            sensitivity: "base",
          })
      );
      return [
        {
          value: DASHBOARD_PROGRAM_FILTER_ALL,
          label: this.t("message.dashboard.programFocusStudyProgramAll"),
        },
        ...sortedPrograms,
      ];
    },
    canSelectDashboardStudyProgram() {
      return (
        this.isDashboardInstitutionFilterActive &&
        this.dashboardProgramStudyProgramOptions.length > 1
      );
    },
    dashboardProgramVisibleCount() {
      return this.selectedCourses.filter(
        (courseCard) => this.getCardRowsForChart(courseCard).length > 0
      ).length;
    },
    dashboardProgramFilteredHiddenCount() {
      if (!this.isDashboardProgramFilterActive) return 0;
      return Math.max(
        0,
        this.selectedCourses.length - this.dashboardProgramVisibleCount
      );
    },
    visibleSelectedCourses() {
      const programFilteredCourses = this.isDashboardProgramFilterActive
        ? this.selectedCourses.filter(
            (courseCard) => this.getCardRowsForChart(courseCard).length > 0
          )
        : this.selectedCourses;
      if (!this.filterOfferedBothSemesters) {
        return programFilteredCourses;
      }
      return programFilteredCourses.filter((courseCard) =>
        this.isCourseOfferedBothSemesters(courseCard)
      );
    },
    hasCourseOfferingMetadata() {
      return this.allCourses.some(
        (course) => typeof course?.offered_both_semesters_recent === "boolean"
      );
    },
    searchableCourses() {
      if (!this.filterOfferedBothSemesters || !this.hasCourseOfferingMetadata) {
        return this.allCourses;
      }
      return this.allCourses.filter((course) =>
        this.isCourseOfferedBothSemestersRecentByCode(course?.course_code)
      );
    },
    filteredOutCourseCount() {
      const baseCourses = this.isDashboardProgramFilterActive
        ? this.selectedCourses.filter(
            (courseCard) => this.getCardRowsForChart(courseCard).length > 0
          )
        : this.selectedCourses;
      return Math.max(
        0,
        baseCourses.length - this.visibleSelectedCourses.length
      );
    },
    hasHistoricalChart() {
      return this.visibleSelectedCourses.some(
        (courseCard) => courseCard.mode === "historical"
      );
    },
    dashboardToolTabs() {
      return [
        { value: "view", label: this.t("message.dashboard.toolsSectionView") },
        {
          value: "style",
          label: this.t("message.dashboard.toolsSectionStyle"),
        },
        {
          value: "advanced",
          label: this.t("message.dashboard.toolsSectionAdvanced"),
        },
        {
          value: "export",
          label: this.t("message.dashboard.toolsSectionExport"),
        },
      ];
    },
    dashboardRiskProfileOptions() {
      return [
        {
          value: "low",
          label: this.t("message.premium.riskLow"),
        },
        {
          value: "balanced",
          label: this.t("message.premium.riskBalanced"),
        },
        {
          value: "high",
          label: this.t("message.premium.riskHigh"),
        },
      ];
    },
    dashboardInsights() {
      return this.visibleSelectedCourses
        .map((courseCard) => this.buildDashboardInsight(courseCard))
        .filter((value) => value != null)
        .sort((left, right) => right.priority - left.priority);
    },
    dashboardShortlist() {
      return this.dashboardInsights.slice(0, 6);
    },
    dashboardSmartPickCount() {
      return this.dashboardInsights.filter(
        (item) => item.quadrant === "smart_picks"
      ).length;
    },
    dashboardAverageStability() {
      return this.getAverageInsightMetric("stabilityScore");
    },
    dashboardAverageOfferProbability() {
      return this.getAverageInsightMetric("offerProbability");
    },
    dashboardRiskRewardPoints() {
      return this.dashboardInsights.map((item) => ({
        ...item,
        riskPercent: this.toMapPercent(item.riskScore),
        rewardPercent: this.toMapPercent(item.rewardScore),
      }));
    },
    riskRewardAxisTicks() {
      return [0, 25, 50, 75, 100];
    },
    chartPaletteOptions() {
      return [
        {
          value: "onlygrades",
          label: this.t("message.dashboard.paletteOnlygrades"),
        },
        {
          value: "slate",
          label: this.t("message.dashboard.paletteSlate"),
        },
        {
          value: "warm",
          label: this.t("message.dashboard.paletteWarm"),
        },
      ];
    },
    isPremiumDashboardUnlocked() {
      if (!IS_PRODUCTION_BUILD) {
        return true;
      }
      if (!this.state.features.premiumEnabled) {
        return false;
      }
      return Boolean(this.state.entitlement?.is_paid);
    },
    canUseStudyPlanFeature() {
      return Boolean(
        this.state.features.premiumEnabled || this.isDevStudyPlanMockEnabled()
      );
    },
    maxDashboardCharts() {
      if (!this.state.features.premiumEnabled) return 4;
      return this.isPremiumDashboardUnlocked
        ? DASHBOARD_MAX_PREMIUM_CHARTS
        : DASHBOARD_MAX_FREE_CHARTS;
    },
    canAddCourse() {
      return this.selectedCourses.length < this.maxDashboardCharts;
    },
    dashboardAtCapacity() {
      return this.selectedCourses.length >= this.maxDashboardCharts;
    },
    showPremiumDashboardBanner() {
      return (
        this.state.features.premiumEnabled &&
        !this.isPremiumDashboardUnlocked &&
        this.selectedCourses.length >= DASHBOARD_MAX_FREE_CHARTS
      );
    },
    canUseAdvancedDashboardTools() {
      if (!this.state.features.premiumEnabled) {
        return true;
      }
      return this.isPremiumDashboardUnlocked;
    },
    dashboardTierLabel() {
      return this.isPremiumDashboardUnlocked
        ? this.t("message.dashboard.tierPremium")
        : this.t("message.dashboard.tierFree");
    },
  },
};
</script>

<style scoped>
.workspace-controls {
  margin-bottom: 16px;
}
.workspace-period {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  color: #53684e;
  font-size: 12px;
}
.workspace-period label {
  display: flex;
  align-items: center;
  gap: 8px;
}
.workspace-period select {
  appearance: auto;
  background: #fff;
  border: 1px solid #d5dfcd;
  border-radius: 5px;
  padding: 8px;
  min-height: 38px;
  max-width: 225px;
  color: #354f39;
  font-size: 12px;
}
.workspace-calculation {
  position: relative;
  order: 3;
  font-size: 12px;
  color: #53684e;
}
.workspace-calculation summary {
  cursor: pointer;
  list-style: none;
  padding: 8px;
  border-radius: 5px;
}
.workspace-calculation summary::-webkit-details-marker {
  display: none;
}
.workspace-calculation > div {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  background: #fff;
  border: 1px solid #d5dfcd;
  border-radius: 8px;
  padding: 14px;
  width: min(280px, 76vw);
  z-index: 30;
  box-shadow: 0 8px 24px #20362a18;
}
.workspace-calculation p {
  font-size: 12px;
  line-height: 1.6;
  margin-top: 8px;
}
.workspace-calculation summary:focus-visible,
.workspace-period select:focus-visible {
  outline: 3px solid #397454;
  outline-offset: 3px;
}
@media (max-width: 600px) {
  .workspace-period {
    margin-left: 0;
    flex: 1;
    min-width: 0;
  }
  .workspace-period select {
    font-size: 16px;
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }
  .workspace-period label {
    width: 100%;
    min-width: 0;
  }
  .workspace-period label > span {
    display: none;
  }
  .workspace-calculation {
    margin-left: auto;
  }
}
.catalog-return-link {
  display: table;
  color: #397454;
  font-size: 12px;
  margin: 0 0 16px;
  text-underline-offset: 4px;
}
.home-container {
  max-width: 1140px;
  margin: auto;
  padding: 0 20px 24px;
  position: relative;
}

.maintenance-hero {
  margin-bottom: 0;
}

.hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(
      160deg,
      rgba(255, 255, 255, 0.78) 0%,
      rgba(245, 244, 239, 0.72) 100%
    ),
    url("~@/assets/generated/matte-hero-bg.svg");
  background-size: 100% 100%, cover;
  background-position: center center, center center;
  background-repeat: no-repeat, no-repeat;
  background-blend-mode: normal, multiply;
  color: var(--og-text);
  padding: 3.5rem 1rem;
  border-radius: 24px;
  margin-bottom: 1.15rem;
  border: 1px solid var(--og-border);
  box-shadow: var(--og-shadow);
}

.hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background: url("~@/assets/generated/matte-hero-overlay.svg") no-repeat right -100px
    top -50px / min(52vw, 520px);
  opacity: 0.72;
  mix-blend-mode: multiply;
  pointer-events: none;
}

.hero-content {
  text-align: center;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 2.35rem;
  margin-bottom: 0.9rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-family: "Work Sans", sans-serif;
}

.hero-subtitle {
  font-size: 1.05rem;
  margin-bottom: 1.4rem;
  font-weight: 400;
  color: var(--og-muted);
}

.hero-premium-row {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.hero-premium-btn {
  border-radius: 999px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}

.hero-premium-copy {
  margin: 0;
  font-size: 0.82rem;
  color: var(--og-muted);
}

.builder-search-bar {
  margin: 0;
  max-width: none;
  padding: 0;
  background: #fff;
  border-radius: 9px;
  border: 1px solid #b4c3ab;
  box-shadow: 0 3px 0 #e5ebde;
}
.builder-search-bar:focus-within {
  border-color: var(--og-primary);
  box-shadow: 0 0 0 3px #32634818;
}
.builder-search-bar :deep(.search-input) {
  border: 0;
  border-radius: 9px;
  background: transparent;
  min-height: 58px;
  padding: 15px 18px 15px 44px;
  font-size: 14px;
  box-shadow: none;
}
.builder-search-bar::before {
  content: "";
  position: absolute;
  width: 13px;
  height: 13px;
  border: 1.7px solid #526950;
  border-radius: 50%;
  left: 17px;
  top: 19px;
  pointer-events: none;
}
.builder-search-bar::after {
  content: "";
  position: absolute;
  width: 6px;
  height: 1.7px;
  background: #526950;
  transform: rotate(45deg);
  left: 28px;
  top: 32px;
  pointer-events: none;
}
.course-builder {
  margin: 0;
  max-width: none;
  border: 0;
  background: transparent;
  border-radius: 0;
  padding: 0;
}
.quick-add-hint {
  display: none;
}

.course-builder-status {
  margin-top: 0.35rem;
  padding: 0.4rem 0.55rem;
  border-radius: 10px;
  border: 1px solid var(--og-border);
  background: var(--og-surface-soft);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.55rem;
  flex-wrap: wrap;
  color: var(--og-muted);
  font-size: 0.82rem;
}

.course-builder-status-error {
  border-color: rgba(185, 28, 28, 0.32);
  color: #991b1b;
  background: rgba(220, 38, 38, 0.06);
}

.course-builder-status-row {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.course-builder-status-spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(100, 116, 139, 0.25);
  border-top-color: var(--og-primary);
  animation: og-spin 0.9s linear infinite;
}

.course-builder-retry-button {
  min-height: 32px;
  margin: 0;
  padding: 0.4rem 0.75rem;
  font-size: 0.76rem;
}

.dashboard-message {
  margin-top: 6px;
  margin-bottom: 0;
  color: var(--og-primary-strong);
  font-size: 0.9rem;
}

.dashboard-loading-message {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.dashboard-loading-spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(100, 116, 139, 0.25);
  border-top-color: var(--og-primary);
  animation: og-spin 0.9s linear infinite;
}

.dashboard-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.1rem;
  margin-bottom: 0.7rem;
}

.dashboard-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.dashboard-badge {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--og-border);
  background: var(--og-primary-soft);
  color: var(--og-primary-strong);
  font-size: 0.8rem;
  font-weight: 600;
}

.dashboard-badge-soft {
  background: var(--og-surface-soft);
  color: var(--og-muted);
}

.dashboard-actions {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.dashboard-premium-note {
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  flex-wrap: wrap;
  border: 1px dashed var(--og-border);
  background: var(--og-surface-soft);
  border-radius: 12px;
  padding: 10px 12px;
}

.dashboard-premium-copy {
  margin: 0;
  text-align: left;
  color: var(--og-muted);
}

.dashboard-capacity-note {
  margin: -2px 0 10px;
  text-align: left;
  color: var(--og-primary-strong);
  font-size: 0.85rem;
  font-weight: 500;
}

.dashboard-filter-empty-note {
  margin: 0 0 10px;
  text-align: left;
  color: var(--og-muted);
  font-size: 0.83rem;
}

.dashboard-collapse-toggle {
  width: 100%;
  border: none;
  background: transparent;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.dashboard-collapse-toggle-inline {
  width: auto;
  border: 1px solid var(--og-border);
  border-radius: 999px;
  background: var(--og-surface);
  color: var(--og-muted);
  font-size: 0.76rem;
  min-height: 30px;
  padding: 0 10px;
}

.dashboard-collapse-toggle-inline:hover {
  border-color: var(--og-primary);
  color: var(--og-primary-strong);
}

.dashboard-tools {
  border: 1px solid var(--og-border);
  border-radius: 12px;
  background: #fff;
  padding: 8px 10px;
}

.dashboard-tools-overlay {
  position: absolute;
  top: 54px;
  right: 12px;
  width: min(520px, calc(100% - 24px));
  max-height: min(76vh, 820px);
  overflow-y: auto;
  margin: 0;
  z-index: 18;
  box-shadow: 0 22px 52px rgba(15, 23, 42, 0.18);
}

.dashboard-tools-fade-enter-active,
.dashboard-tools-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dashboard-tools-fade-enter-from,
.dashboard-tools-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.dashboard-tools-header {
  margin-bottom: 0.45rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.dashboard-tools-title {
  margin: 0;
  text-align: left;
  color: var(--og-text);
  font-size: 0.9rem;
  font-weight: 600;
}

.dashboard-tools-subtitle {
  margin: 2px 0 0;
  text-align: left;
  color: var(--og-muted);
  font-size: 0.79rem;
}

.dashboard-tools-summary {
  margin-top: 6px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.dashboard-summary-chip {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border-radius: 999px;
  border: 1px solid var(--og-border);
  background: var(--og-surface);
  color: var(--og-muted);
  padding: 0 8px;
  font-size: 0.73rem;
  white-space: nowrap;
}

.dashboard-summary-chip-help {
  gap: 4px;
  cursor: help;
}

.dashboard-summary-help-icon {
  color: var(--og-muted);
}

.dashboard-tools-nav {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.dashboard-tools-tab {
  border: 1px solid var(--og-border);
  background: var(--og-surface);
  color: var(--og-muted);
  border-radius: 999px;
  font-size: 0.76rem;
  min-height: 30px;
  padding: 0 10px;
  cursor: pointer;
}

.dashboard-tools-tab:hover {
  border-color: var(--og-primary);
  color: var(--og-primary-strong);
}

.dashboard-tools-tab-active {
  background: var(--og-primary-soft);
  color: var(--og-primary-strong);
  border-color: var(--og-primary);
}

.dashboard-tools-grid {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1fr;
}

.dashboard-tools-grid-single {
  grid-template-columns: 1fr;
}

.dashboard-tools-group {
  border: 1px solid var(--og-border);
  border-radius: 10px;
  background: var(--og-surface);
  padding: 9px;
  min-height: 100%;
}

.dashboard-tools-label {
  margin: 0 0 6px;
  text-align: left;
  color: var(--og-muted);
  font-size: 0.78rem;
  font-weight: 600;
}

.dashboard-tools-note {
  margin: 0 0 8px;
  text-align: left;
  color: var(--og-muted);
  font-size: 0.78rem;
}

.dashboard-tools-lock {
  margin: 6px 0 0;
  text-align: left;
  color: var(--og-muted);
  font-size: 0.77rem;
}

.dashboard-tools-row {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.dashboard-tools-row-program-focus .dashboard-tools-select {
  flex: 1 1 250px;
  min-width: 220px;
}

.dashboard-tools-select {
  width: 100%;
  margin: 0;
  min-height: 38px;
}

.dashboard-tool-buttons {
  justify-content: flex-start;
  margin-bottom: 0;
}

.risk-reward-map {
  margin-top: 8px;
  border: 1px solid var(--og-border);
  border-radius: 10px;
  background: var(--og-surface-soft);
  padding: 8px;
}

.risk-reward-map-title {
  margin: 0 0 6px;
  text-align: left;
  color: var(--og-muted);
  font-size: 0.76rem;
  font-weight: 600;
}

.risk-reward-map-subtitle {
  margin: 0 0 8px;
  text-align: left;
  color: var(--og-muted);
  font-size: 0.73rem;
}

.risk-reward-map-hint {
  margin: 0 0 8px;
  text-align: left;
  color: var(--og-primary-strong);
  font-size: 0.7rem;
  font-weight: 500;
}

.risk-reward-map-canvas {
  position: relative;
  height: 210px;
  border: 1px solid var(--og-border);
  border-radius: 10px;
  overflow: hidden;
  background: linear-gradient(
      to right,
      rgba(14, 116, 144, 0.08) 0%,
      rgba(14, 116, 144, 0.08) 50%,
      rgba(185, 28, 28, 0.07) 50%,
      rgba(185, 28, 28, 0.07) 100%
    ),
    linear-gradient(
      to top,
      rgba(22, 163, 74, 0.08) 0%,
      rgba(22, 163, 74, 0.08) 50%,
      rgba(217, 119, 6, 0.08) 50%,
      rgba(217, 119, 6, 0.08) 100%
    ),
    var(--og-surface);
}

.risk-reward-map-canvas::before,
.risk-reward-map-canvas::after {
  content: "";
  position: absolute;
  background: rgba(100, 116, 139, 0.26);
}

.risk-reward-map-canvas::before {
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
}

.risk-reward-map-canvas::after {
  left: 0;
  top: 50%;
  width: 100%;
  height: 1px;
}

.risk-reward-axis-tick {
  position: absolute;
  z-index: 1;
  color: var(--og-muted);
  font-size: 0.62rem;
  opacity: 0.82;
  pointer-events: none;
}

.risk-reward-axis-tick-x {
  bottom: 18px;
  transform: translateX(-50%);
}

.risk-reward-point {
  position: absolute;
  z-index: 3;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  min-width: 30px;
  border-radius: 999px;
  border: 1px solid var(--og-border);
  padding: 0 6px;
  background: var(--og-surface);
  color: var(--og-text);
  font-size: 0.68rem;
  font-weight: 600;
  max-width: 68px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease,
    border-color 0.16s ease;
}

.risk-reward-point:hover {
  transform: translate(-50%, -50%) scale(1.05);
  border-color: var(--og-primary);
  box-shadow: 0 0 0 2px rgba(47, 111, 235, 0.14);
}

.risk-reward-point:focus-visible {
  outline: none;
  border-color: var(--og-primary-strong);
  box-shadow: 0 0 0 3px rgba(47, 111, 235, 0.2);
}

.risk-reward-point-smart_picks {
  background: rgba(22, 163, 74, 0.16);
  color: #166534;
}

.risk-reward-point-safe_choices {
  background: rgba(37, 99, 235, 0.16);
  color: #1d4ed8;
}

.risk-reward-point-high_risk_high_reward {
  background: rgba(217, 119, 6, 0.18);
  color: #b45309;
}

.risk-reward-point-review_carefully {
  background: rgba(220, 38, 38, 0.16);
  color: #b91c1c;
}

.risk-reward-axis {
  position: absolute;
  z-index: 2;
  color: var(--og-muted);
  font-size: 0.66rem;
}

.risk-reward-axis-x-left {
  left: 6px;
  bottom: 4px;
}

.risk-reward-axis-x-right {
  right: 6px;
  bottom: 4px;
}

.risk-reward-axis-y-top {
  left: 6px;
  top: 4px;
}

.risk-reward-axis-y-bottom {
  left: 6px;
  bottom: 24px;
}

.risk-reward-axis-title {
  position: absolute;
  z-index: 2;
  color: var(--og-muted);
  font-size: 0.67rem;
  font-weight: 600;
  opacity: 0.94;
}

.risk-reward-axis-title-x {
  left: 50%;
  bottom: 2px;
  transform: translateX(-50%);
}

.risk-reward-axis-title-y {
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  writing-mode: vertical-rl;
  text-orientation: mixed;
}

.risk-reward-method {
  margin-top: 8px;
  border: 1px solid var(--og-border);
  border-radius: 10px;
  background: var(--og-surface);
  padding: 8px;
}

.risk-reward-method-title {
  margin: 0 0 5px;
  text-align: left;
  color: var(--og-text);
  font-size: 0.74rem;
  font-weight: 600;
}

.risk-reward-method-line {
  margin: 0 0 3px;
  text-align: left;
  color: var(--og-muted);
  font-size: 0.72rem;
}

.risk-reward-method-line:last-child {
  margin-bottom: 0;
}

.dashboard-shortlist {
  margin-top: 8px;
  display: grid;
  gap: 6px;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
}

.dashboard-shortlist-item {
  text-align: left;
  border: 1px solid var(--og-border);
  border-radius: 10px;
  background: var(--og-surface-soft);
  padding: 8px 9px;
}

.dashboard-shortlist-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.dashboard-shortlist-quadrant {
  display: inline-flex;
  border: 1px solid var(--og-border);
  border-radius: 999px;
  padding: 0 8px;
  min-height: 22px;
  align-items: center;
  font-size: 0.72rem;
}

.dashboard-shortlist-quadrant-smart_picks {
  color: #166534;
  background: rgba(22, 163, 74, 0.12);
}

.dashboard-shortlist-quadrant-safe_choices {
  color: #1d4ed8;
  background: rgba(37, 99, 235, 0.12);
}

.dashboard-shortlist-quadrant-high_risk_high_reward {
  color: #b45309;
  background: rgba(217, 119, 6, 0.14);
}

.dashboard-shortlist-quadrant-review_carefully {
  color: #b91c1c;
  background: rgba(220, 38, 38, 0.12);
}

.dashboard-shortlist-meta {
  margin: 4px 0 0;
  color: var(--og-muted);
  font-size: 0.78rem;
}

.dashboard-shortlist-semesters {
  color: var(--og-primary-strong);
}

.dashboard-shortlist-actions {
  margin-top: 6px;
  display: flex;
  justify-content: flex-end;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(320px, 1fr));
  gap: 0.85rem;
}

.dashboard-grid-single {
  grid-template-columns: 1fr;
}

.chart-panel {
  border: 1px solid var(--og-border);
  border-radius: 10px;
  background: #fff;
  padding: 20px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.chart-panel-focused {
  border-color: var(--og-primary);
  box-shadow: 0 0 0 3px rgba(57, 116, 84, 0.16);
}

.chart-panel-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.35rem;
}

.chart-panel-title-wrap {
  flex: 1 1 200px;
  min-width: 0;
  text-align: left;
}

.chart-panel-title {
  margin: 0;
  color: var(--og-text);
  font-family: "Work Sans", sans-serif;
  font-size: 1.02rem;
  line-height: 1.25;
}

.chart-panel-subtitle {
  margin: 3px 0 0;
  color: var(--og-muted);
  font-size: 0.82rem;
}

.chart-panel-quick-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: auto;
}

.chart-panel-inline-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
}

.panel-select {
  width: 100%;
  margin: 0;
  min-height: 34px;
}

.panel-select-compact {
  min-height: 32px;
  max-width: 190px;
  padding: 0.35rem 0.65rem;
}

.panel-select-inline {
  margin: 0;
}

.panel-plan-btn-compact,
.panel-remove-btn-compact {
  min-height: 30px;
  font-size: 0.74rem;
  padding: 0 9px;
}

.panel-remove-btn {
  min-height: 34px;
  border: 1px solid var(--og-border);
  background: var(--og-surface);
  color: var(--og-muted);
  border-radius: 999px;
  padding: 0 10px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 0.78rem;
}

.panel-remove-btn:hover {
  border-color: var(--og-primary);
  color: var(--og-primary-strong);
}

.panel-plan-btn {
  min-height: 34px;
  border: 1px solid var(--og-border);
  background: var(--og-surface-soft);
  color: var(--og-muted);
  border-radius: 999px;
  padding: 0 10px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 0.78rem;
}

.panel-plan-btn:hover {
  border-color: var(--og-primary);
  color: var(--og-primary-strong);
}

.panel-plan-btn:disabled {
  opacity: 0.7;
  cursor: default;
}

.panel-plan-btn-saved {
  background: var(--og-primary-soft);
  color: var(--og-primary-strong);
  border-color: var(--og-primary);
}

.panel-plan-btn-short {
  min-height: 28px;
  font-size: 0.72rem;
  padding: 0 9px;
}

.distribution-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin-bottom: 0.55rem;
  width: 100%;
}

.panel-distribution-controls {
  margin-top: 2px;
}

.term-nav-btn {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 999px;
  border: 1px solid var(--og-border);
  background: var(--og-surface-soft);
  color: var(--og-text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s, color 0.2s;
}

.term-nav-btn:hover:not(:disabled) {
  border-color: var(--og-primary);
  background: var(--og-primary-soft);
  color: var(--og-primary-strong);
}

.term-nav-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.dropdown {
  margin: 0.25rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--og-border);
  border-radius: 10px;
  background: var(--og-surface-soft);
  color: var(--og-text);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.term-dropdown {
  margin: 0;
  min-width: 220px;
  max-width: 320px;
  flex: 0 1 280px;
  text-align: center;
}

.dropdown:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(57, 116, 84, 0.16);
}

.toggle-buttons {
  display: flex;
  justify-content: center;
  margin-bottom: 0.1rem;
  flex-wrap: wrap;
}

.toggle-button {
  margin: 0.25rem;
  padding: 0.5rem 0.95rem;
  background-color: var(--og-surface-soft);
  color: var(--og-text);
  border: 1px solid var(--og-border);
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background-color 0.2s, border-color 0.2s;
  min-height: 40px;
}

.toggle-button.active {
  background: var(--og-primary-soft);
  color: var(--og-primary-strong);
  border-color: var(--og-primary);
}

.toggle-button:hover {
  border-color: var(--og-primary);
}

.selection-status {
  padding: 24px;
  color: var(--og-muted);
}

.result-section {
  margin-top: 0.85rem;
}

.result-card {
  position: relative;
  border-radius: 18px !important;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0;
}

@keyframes og-spin {
  to {
    transform: rotate(360deg);
  }
}

.chart {
  margin: 0;
  padding: 0;
  background: transparent;
  border-radius: 12px;
  box-shadow: none;
  max-width: 100%;
}

.chart-wrapper {
  position: relative;
  min-height: 320px;
}

.card-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  margin: 0;
  color: var(--og-muted);
}

@media (max-width: 900px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  .dashboard-tools-overlay {
    position: static;
    width: 100%;
    max-height: none;
    margin-bottom: 0.6rem;
    box-shadow: none;
  }
  .dashboard-tools-grid {
    grid-template-columns: 1fr;
  }
  .dashboard-shortlist {
    grid-template-columns: 1fr;
  }
  .risk-reward-map-canvas {
    height: 190px;
  }
  .risk-reward-axis-tick {
    font-size: 0.58rem;
  }
}

@media (max-width: 600px) {
  .home-container {
    padding: 0.4rem 0.35rem 0.75rem;
  }
  .hero {
    padding: 2rem 0.75rem;
    border-radius: 16px;
    margin-bottom: 0.8rem;
  }
  .hero::before {
    background-size: min(74vw, 420px);
    background-position: right -130px top -24px;
  }
  .hero-title {
    font-size: 1.72rem;
    line-height: 1.2;
  }
  .hero-subtitle {
    font-size: 0.95rem;
    margin-bottom: 1rem;
  }
  .hero-premium-copy {
    font-size: 0.78rem;
    max-width: 280px;
  }
  .course-builder {
    padding: 0;
    margin-bottom: 0;
  }
  .course-builder-status {
    padding: 0.45rem 0.5rem;
  }
  .course-builder-retry-button {
    width: 100%;
    justify-content: center;
  }
  .dashboard-actions,
  .dashboard-tool-buttons,
  .dashboard-tools-row {
    justify-content: flex-start;
    width: 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 0.35rem;
    margin-bottom: 0.45rem;
    padding: 0 0 6px;
    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;
  }
  .dashboard-tools-row-program-focus {
    flex-wrap: wrap;
    overflow-x: visible;
    padding-bottom: 0;
  }
  .dashboard-tools-row-program-focus .dashboard-tools-select {
    min-width: 0;
    flex: 1 1 100%;
  }
  .dashboard-tools {
    padding: 9px;
  }
  .dashboard-tools-group {
    padding: 9px;
  }
  .dashboard-tools-summary {
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 2px;
  }
  .dashboard-summary-chip {
    flex: 0 0 auto;
  }
  .dashboard-collapse-toggle {
    padding: 8px 9px;
  }
  .dashboard-collapse-toggle-inline {
    min-height: 32px;
    padding: 0 10px;
  }
  .dashboard-tools-nav {
    width: 100%;
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 2px;
  }
  .dashboard-tools-tab {
    white-space: nowrap;
    flex: 0 0 auto;
  }
  .dashboard-actions .toggle-button,
  .dashboard-tool-buttons .toggle-button,
  .dashboard-tools-row .toggle-button {
    margin: 0;
    flex: 0 0 auto;
    white-space: nowrap;
    font-size: 0.79rem;
    padding: 0.46rem 0.8rem;
  }
  .chart-panel-header {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.35rem;
  }
  .chart-panel-title-wrap {
    width: 100%;
    min-width: 0;
  }
  .chart-panel-inline-controls {
    width: 100%;
    order: 2;
    justify-content: stretch;
  }
  .panel-select-inline {
    max-width: none;
    width: 100%;
  }
  .chart-panel-quick-actions {
    order: 3;
    margin-left: auto;
  }
  .panel-select-compact {
    max-width: none;
    width: 100%;
  }
  .distribution-controls {
    justify-content: space-between;
    gap: 0.3rem;
    margin-bottom: 0.45rem;
  }
  .dashboard-premium-note {
    padding: 9px 10px;
  }
  .dashboard-premium-copy {
    font-size: 0.88rem;
  }
  .risk-reward-map-canvas {
    height: 180px;
  }
  .risk-reward-map-subtitle {
    font-size: 0.68rem;
  }
  .risk-reward-map-hint {
    font-size: 0.65rem;
  }
  .risk-reward-axis {
    font-size: 0.6rem;
  }
  .risk-reward-axis-title {
    font-size: 0.58rem;
  }
  .risk-reward-axis-tick {
    font-size: 0.54rem;
  }
  .risk-reward-method-title {
    font-size: 0.7rem;
  }
  .risk-reward-method-line {
    font-size: 0.67rem;
  }
  .risk-reward-point {
    min-width: 28px;
    max-width: 60px;
    font-size: 0.6rem;
    height: 22px;
    padding: 0 5px;
  }
  .term-nav-btn {
    width: 38px;
    height: 38px;
    min-width: 38px;
  }
  .term-dropdown {
    margin: 0;
    width: auto;
    min-width: 0;
    max-width: none;
    flex: 1;
    min-height: 40px;
    font-size: 0.9rem;
  }
  .selection-status {
    padding: 24px;
    color: var(--og-muted);
  }

  .result-section {
    padding: 0.15rem;
  }
  .result-card {
    padding: 8px 8px 4px;
  }
  .chart-wrapper {
    min-height: 300px;
  }
}

@media (max-width: 380px) {
  .term-dropdown {
    font-size: 0.86rem;
  }
  .hero-title {
    font-size: 1.58rem;
  }
}

/* Shared workspace controls stay separate from the individual course cards. */
.workspace-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  margin-bottom: 18px;
  text-align: left;
}
.workspace-status {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;
  font-size: 11px;
  color: #6b7866;
}
.workspace-status strong {
  color: #354f39;
  font-weight: 600;
}
.workspace-status > span:last-child {
  font-size: 10px;
}
.workspace-dot {
  width: 6px;
  height: 6px;
  background: #5d8957;
  border-radius: 50%;
}
.workspace-actions {
  display: flex;
  gap: 9px;
  align-items: center;
}
.workspace-tool {
  border: 1px solid #d7e0cf;
  background: #fff;
  padding: 9px 13px;
  border-radius: 6px;
  font-size: 11px;
  white-space: nowrap;
}
.workspace-tool > span {
  margin-left: 8px;
}
.workspace-menu {
  position: relative;
}
.workspace-menu summary {
  display: grid;
  place-items: center;
  height: 34px;
  width: 34px;
  background: #fff;
  border: 1px solid #d7e0cf;
  border-radius: 6px;
  cursor: pointer;
  list-style: none;
}
.workspace-menu summary::-webkit-details-marker {
  display: none;
}
.workspace-menu > div {
  position: absolute;
  top: 42px;
  right: 0;
  z-index: 25;
  background: #fff;
  padding: 6px;
  width: 190px;
  border-radius: 8px;
  border: 1px solid #d7e0cf;
  box-shadow: 0 12px 30px #20362a18;
}
.workspace-menu button {
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px;
  font-size: 11px;
  border-radius: 4px;
}
.workspace-menu button:hover {
  background: #edf2e5;
}
.workspace-menu button:disabled {
  opacity: 0.4;
}
.workspace-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
  background: #edf1e5;
  border: 1px solid #dde5d4;
  border-radius: 10px;
  padding: 10px;
}
.workspace-modes {
  display: flex;
  gap: 3px;
}
.workspace-modes button {
  border-radius: 6px;
  padding: 10px 18px;
  font-size: 12px;
  font-weight: 500;
  color: #5b7055;
}
.workspace-modes button[aria-pressed="true"] {
  background: #284c35;
  color: #fff;
}
.workspace-range {
  display: flex;
  gap: 9px;
  align-items: center;
  font-size: 10px;
  color: #5b7055;
  margin-left: auto;
}
.workspace-range select,
.shared-term select {
  appearance: auto;
  border: 1px solid #d5dfcd;
  background: #fff;
  border-radius: 5px;
  padding: 8px;
  font-size: 11px;
  color: #354f39;
}
.workspace-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #53684e;
}
.workspace-check input {
  accent-color: #326348;
  width: 14px;
  height: 14px;
}
.workspace-method {
  color: #6d7a66;
  text-align: left;
  font-size: 10px;
  line-height: 1.6;
  margin: 12px 1px 20px;
}
.shared-term {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  border-bottom: 1px solid #e0e7d9;
  padding: 15px 0;
  font-size: 10px;
  color: #64765b;
  text-align: left;
}
.shared-term label {
  display: flex;
  align-items: center;
  gap: 10px;
}
.shared-term select {
  max-width: 240px;
}
.dashboard-grid {
  gap: 20px;
  align-items: start;
}
.dashboard-tools-overlay {
  top: 130px;
}
.workspace-toolbar button,
.workspace-controls button {
  cursor: pointer;
}
.workspace-toolbar button:focus-visible,
.workspace-controls button:focus-visible,
.workspace-controls select:focus-visible,
.workspace-menu summary:focus-visible {
  outline: 3px solid #397454;
  outline-offset: 3px;
}
@media (max-width: 900px) {
  .workspace-status > span:last-child {
    width: 100%;
    margin-left: 15px;
  }
  .workspace-controls {
    gap: 12px;
  }
  .workspace-modes button {
    padding-inline: 13px;
  }
  .workspace-range {
    margin-left: 0;
  }
  .dashboard-tools-overlay {
    position: static;
  }
}
@media (max-width: 600px) {
  .workspace-toolbar {
    align-items: start;
  }
  .workspace-tool {
    padding: 8px;
  }
  .workspace-modes {
    width: 100%;
  }
  .workspace-modes button {
    flex: 1;
  }
  .workspace-controls {
    gap: 14px;
  }
  .workspace-range {
    flex: 1;
  }
  .workspace-status {
    font-size: 10px;
  }
  .workspace-status > span:last-child {
    font-size: 9px;
  }
  .shared-term label {
    flex-wrap: wrap;
  }
  .shared-term select {
    max-width: 100%;
  }
}
@media (min-width: 761px) and (max-width: 900px) {
  .dashboard-grid:not(.dashboard-grid-single) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.workspace-layout {
  display: flex;
  gap: 3px;
  padding: 3px;
  border: 1px solid #d8e3ce;
  border-radius: 7px;
  background: #edf2e5;
  margin-left: auto;
}
.workspace-layout button {
  font-size: 11px;
  color: #617657;
  padding: 8px 12px;
  border-radius: 4px;
  white-space: nowrap;
}
.workspace-layout button[aria-pressed="true"] {
  background: #fff;
  color: #294e33;
  box-shadow: 0 1px 3px #27422412;
}
.workspace-layout button:disabled {
  opacity: 0.4;
  cursor: default;
}
.workspace-layout button:focus-visible {
  outline: 3px solid #326348;
  outline-offset: 3px;
}
@media (max-width: 600px) {
  .workspace-toolbar {
    flex-wrap: wrap;
  }
  .workspace-status {
    flex: 1;
  }
  .workspace-layout {
    order: 3;
    margin-left: 0;
    width: 100%;
  }
  .workspace-layout button {
    flex: 1;
  }
}

/* Readable controls keep the chart workspace compact at intermediate widths. */
.workspace-method summary {
  cursor: pointer;
  color: #5d7653;
  font-size: 11px;
  width: fit-content;
}
.workspace-method p {
  margin-top: 8px;
  max-width: 820px;
}
.workspace-controls {
  gap: 12px;
  padding: 9px;
}
.workspace-range,
.workspace-check,
.shared-term {
  font-size: 12px;
}
.workspace-range select,
.shared-term select {
  font-size: 12px;
  min-height: 38px;
}
.workspace-check input {
  width: 16px;
  height: 16px;
}
.workspace-method {
  margin-bottom: 16px;
}
@media (max-width: 600px) {
  .workspace-toolbar {
    gap: 10px;
  }
  .workspace-status strong {
    font-size: 12px;
  }
  .workspace-status > span:last-child {
    display: none;
  }
  .workspace-layout button {
    min-height: 40px;
    font-size: 12px;
  }
  .workspace-range select,
  .shared-term select {
    font-size: 16px;
    max-width: 100%;
    min-width: 0;
  }
  .workspace-check {
    min-height: 34px;
  }
  .shared-term {
    gap: 8px;
    padding: 12px 0;
  }
  .shared-term label {
    width: 100%;
    display: grid;
    gap: 7px;
  }
  .shared-term select {
    width: 100%;
  }
}
</style>
